import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import useAnimationEngine from "../../../hooks/useAnimationEngine.js";
import useAudioEngine from "../../../hooks/useAudioEngine.js";
import useAnimationExport from "../../../hooks/useAnimationExport.js";
import { formatTime } from "../../../utils/formatTime.js";
import { updateAudioMarkers } from "../../../services/animation.service.js";
import AudioWaveform from "./AudioWaveform.jsx";
import EditorToolsBar from "./EditorToolsBar.jsx";
import {
  FiBox,
  FiPlay,
  FiPause,
  FiRotateCcw,
  FiSettings,
  FiSkipBack,
  FiSkipForward,
  FiDownload,
  FiX,
  FiMaximize,
  FiMapPin,
  FiDroplet,
  FiVideo,
} from "react-icons/fi";
import "../../../styles/studio/animation/editorDisplay.css";

const WORLD_WIDTH = 1600;
const WORLD_HEIGHT = 900;
const WORLD_CENTER_X = WORLD_WIDTH / 2;
const WORLD_CENTER_Y = WORLD_HEIGHT / 2;
const MIN_CUSTOM_FRAME_SIZE = 80;
const CUSTOM_FRAME_SNAP_THRESHOLD = 18;
const EXPORT_TYPE_OPTIONS = [
  { value: "video", label: "Video" },
  { value: "image", label: "Image" },
];
const VIDEO_EXPORT_LAYOUT_OPTIONS = [
  { value: "landscape", label: "Landscape" },
  { value: "portrait_fit", label: "Portrait fit" },
  { value: "portrait_crop", label: "Portrait crop" },
  { value: "portrait_custom", label: "Portrait custom" },
];
const IMAGE_EXPORT_LAYOUT_OPTIONS = [
  ...VIDEO_EXPORT_LAYOUT_OPTIONS,
  { value: "custom_size", label: "Custom size" },
];
const MIN_PREVIEW_ZOOM = 1;
const MAX_PREVIEW_ZOOM = 4;
const SHAPE_TOOL_MIN_SIZE = 24;
const SHAPE_TOOL_DEFAULT_FILL = "#22d3ee";
const SHAPE_TOOL_DEFAULT_TEXT = "#ffffff";

const parseTimeInput = (value) => {
  const parsed = Number.parseFloat(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const parseEditableNumber = (value) => {
  const parsed = Number(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDslNumber = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  const rounded = Math.round(parsed * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/\.?0+$/, "");
};

const escapeDslString = (value) =>
  String(value ?? "").replace(/\\/g, "\\\\").replace(/"/g, '\\"');

const clampCustomFrame = (frame) => {
  const width = Math.min(
    WORLD_WIDTH,
    Math.max(MIN_CUSTOM_FRAME_SIZE, Number(frame.width) || MIN_CUSTOM_FRAME_SIZE),
  );
  const height = Math.min(
    WORLD_HEIGHT,
    Math.max(MIN_CUSTOM_FRAME_SIZE, Number(frame.height) || MIN_CUSTOM_FRAME_SIZE),
  );
  const x = Math.min(
    WORLD_WIDTH - width,
    Math.max(0, Number(frame.x) || 0),
  );
  const y = Math.min(
    WORLD_HEIGHT - height,
    Math.max(0, Number(frame.y) || 0),
  );
  return { x, y, width, height };
};

const createDefaultCustomFrame = () =>
  clampCustomFrame({
    x: 500,
    y: 60,
    width: 600,
    height: 780,
  });

const drawRoundedRect = (
  ctx,
  x,
  y,
  w,
  h,
  { tl = 0, tr = 0, br = 0, bl = 0 } = {},
) => {
  const rtlRaw = Math.max(0, tl);
  const rtrRaw = Math.max(0, tr);
  const rbrRaw = Math.max(0, br);
  const rblRaw = Math.max(0, bl);

  const factor = Math.min(
    1,
    w / Math.max(0.0001, rtlRaw + rtrRaw),
    w / Math.max(0.0001, rblRaw + rbrRaw),
    h / Math.max(0.0001, rtlRaw + rblRaw),
    h / Math.max(0.0001, rtrRaw + rbrRaw),
  );

  const rtl = rtlRaw * factor;
  const rtr = rtrRaw * factor;
  const rbr = rbrRaw * factor;
  const rbl = rblRaw * factor;

  ctx.beginPath();
  ctx.moveTo(x + rtl, y);
  ctx.lineTo(x + w - rtr, y);
  ctx.arcTo(x + w, y, x + w, y + rtr, rtr);
  ctx.lineTo(x + w, y + h - rbr);
  ctx.arcTo(x + w, y + h, x + w - rbr, y + h, rbr);
  ctx.lineTo(x + rbl, y + h);
  ctx.arcTo(x, y + h, x, y + h - rbl, rbl);
  ctx.lineTo(x, y + rtl);
  ctx.arcTo(x, y, x + rtl, y, rtl);
  ctx.closePath();
};

const buildShapePath = (ctx, rect, component, cameraZoom, scale) => {
  const shape = component.shape || "rect";
  if (shape === "circle" || shape === "ellipse") {
    ctx.beginPath();
    ctx.ellipse(
      rect.x + rect.w / 2,
      rect.y + rect.h / 2,
      Math.max(0.0001, rect.w / 2),
      Math.max(0.0001, rect.h / 2),
      0,
      0,
      Math.PI * 2,
    );
    ctx.closePath();
    return;
  }

  if (shape === "diamond") {
    ctx.beginPath();
    ctx.moveTo(rect.x + rect.w / 2, rect.y);
    ctx.lineTo(rect.x + rect.w, rect.y + rect.h / 2);
    ctx.lineTo(rect.x + rect.w / 2, rect.y + rect.h);
    ctx.lineTo(rect.x, rect.y + rect.h / 2);
    ctx.closePath();
    return;
  }

  if (shape === "triangle") {
    ctx.beginPath();
    ctx.moveTo(rect.x + rect.w / 2, rect.y);
    ctx.lineTo(rect.x + rect.w, rect.y + rect.h);
    ctx.lineTo(rect.x, rect.y + rect.h);
    ctx.closePath();
    return;
  }

  drawRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, {
    tl: Number(component.radiusTopLeft || 0) * cameraZoom * scale,
    tr: Number(component.radiusTopRight || 0) * cameraZoom * scale,
    br: Number(component.radiusBottomRight || 0) * cameraZoom * scale,
    bl: Number(component.radiusBottomLeft || 0) * cameraZoom * scale,
  });
};

const applyLineStyle = (ctx, type) => {
  if (type === "dashed") {
    ctx.setLineDash([6, 4]);
  } else if (type === "dotted") {
    ctx.setLineDash([1, 3]);
  } else {
    ctx.setLineDash([]);
  }
};

const getBorderPoint = (from, to) => {
  const dx = to.cx - from.cx;
  const dy = to.cy - from.cy;

  const hw = from.width / 2;
  const hh = from.height / 2;
  const scale =
    Math.abs(dy) * hw > Math.abs(dx) * hh
      ? hh / Math.max(0.0001, Math.abs(dy))
      : hw / Math.max(0.0001, Math.abs(dx));

  return {
    x: from.cx + dx * scale,
    y: from.cy + dy * scale,
  };
};

const breakLongWord = (ctx, word, maxWidth) => {
  const parts = [];
  let current = "";
  for (const ch of word) {
    const next = current + ch;
    if (current && ctx.measureText(next).width > maxWidth) {
      parts.push(current);
      current = ch;
    } else {
      current = next;
    }
  }
  if (current) parts.push(current);
  return parts;
};

const wrapTextLines = (ctx, text, maxWidth) => {
  const safeWidth = Math.max(1, maxWidth);
  const paragraphs = String(text ?? "").split("\n");
  const lines = [];

  paragraphs.forEach((p, pIndex) => {
    if (!p.length) {
      lines.push("");
      lines.push("");
      return;
    }

    const tokens = p.split(/(\s+)/);
    let line = "";

    tokens.forEach((token) => {
      const candidate = line ? line + token : token;
      if (ctx.measureText(candidate).width <= safeWidth) {
        line = candidate;
        return;
      }

      if (line) {
        lines.push(line);
        line = "";
      }

      if (ctx.measureText(token).width <= safeWidth) {
        line = token;
      } else {
        const chunks = breakLongWord(ctx, token, safeWidth);
        chunks.forEach((chunk, idx) => {
          if (idx < chunks.length - 1) {
            lines.push(chunk);
          } else {
            line = chunk;
          }
        });
      }
    });

    if (line) lines.push(line);
    if (pIndex < paragraphs.length - 1) lines.push("");
  });

  return lines.length ? lines : [""];
};

const drawTextLine = (ctx, text, x, y, letterSpacing = 0) => {
  const spacing = Number(letterSpacing || 0);
  if (Math.abs(spacing) < 0.0001) {
    ctx.fillText(text, x, y);
    return;
  }

  const content = String(text ?? "");
  const chars = [...content];
  if (!chars.length) return;

  let currentX = x;
  if (ctx.textAlign === "center") {
    const totalWidth =
      ctx.measureText(content).width + Math.max(0, chars.length - 1) * spacing;
    currentX -= totalWidth / 2;
    ctx.textAlign = "left";
  } else if (ctx.textAlign === "right") {
    const totalWidth =
      ctx.measureText(content).width + Math.max(0, chars.length - 1) * spacing;
    currentX -= totalWidth;
    ctx.textAlign = "left";
  }

  chars.forEach((char, index) => {
    ctx.fillText(char, currentX, y);
    currentX += ctx.measureText(char).width;
    if (index < chars.length - 1) currentX += spacing;
  });
};

const applyShadowStyle = (ctx, component, cameraZoom, scale) => {
  ctx.shadowColor = component.shadowColor || "#00000000";
  ctx.shadowBlur = Number(component.shadowBlur || 0) * cameraZoom * scale;
  ctx.shadowOffsetX = Number(component.shadowOffsetX || 0) * cameraZoom * scale;
  ctx.shadowOffsetY = -Number(component.shadowOffsetY || 0) * cameraZoom * scale;
};

const clearShadowStyle = (ctx) => {
  ctx.shadowColor = "#00000000";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
};

const rotatePointAround = (point, center, angleDegrees) => {
  const radians = (angleDegrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;

  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
};

const clampWorldCoordinate = (value, max) =>
  Math.min(max, Math.max(0, Number(value) || 0));

const AUDIO_MARKER_SAVE_DELAY_MS = 700;

const normalizeAudioMarkers = (markers = []) =>
  markers
    .map((marker, index) => {
      const markerId =
        String(marker?.markerId || marker?.id || `marker-${index + 1}`).trim();
      const label = String(marker?.label || "").trim();
      const time = Math.max(0, Number(marker?.time) || 0);
      if (!markerId || !label) return null;
      return {
        markerId,
        label,
        time,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);

const buildAudioMarkersPayload = (markers = []) =>
  markers
    .map((marker, index) => ({
      markerId: String(marker?.markerId || marker?.id || `marker-${index + 1}`).trim(),
      time: Math.max(0, Number(marker?.time) || 0),
      label: String(marker?.label || "").trim() || `M${index + 1}`,
    }))
    .filter((marker) => marker.markerId)
    .sort((a, b) => a.time - b.time);

const getNextMarkerCounter = (markers = []) =>
  markers.reduce((max, marker) => {
    const match = String(marker?.label || "").match(/^M(\d+)$/i);
    return match ? Math.max(max, Number(match[1]) || 0) : max;
  }, 0) + 1;

const buildAudioMarkersSnapshot = (markers = [], lockedPlaybackMarkerId = null) =>
  JSON.stringify({
    markers: buildAudioMarkersPayload(markers),
    lockedPlaybackMarkerId,
  });

const formatWorldCoordinate = (value) => {
  const rounded = Math.round((Number(value) || 0) * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/\.?0+$/, "");
};

const buildShapeDraftFromComponent = (component, overrides = {}) => ({
  shapeId:
    overrides.shapeId ??
    component?.animationComponentId ??
    component?.animationComponentName ??
    `shape-${Date.now()}`,
  isNew: Boolean(overrides.isNew),
  name: String(
    overrides.name ??
      component?.animationComponentName ??
      component?.componentName ??
      "shape",
  ),
  shape: String(overrides.shape ?? component?.shape ?? "rect"),
  visibility: String(overrides.visibility ?? component?.visibility ?? true),
  x: formatWorldCoordinate(overrides.x ?? component?.x ?? 0),
  y: formatWorldCoordinate(overrides.y ?? component?.y ?? 0),
  z: formatWorldCoordinate(overrides.z ?? component?.z ?? 0),
  width: formatWorldCoordinate(overrides.width ?? component?.width ?? 160),
  height: formatWorldCoordinate(overrides.height ?? component?.height ?? 120),
  originX: formatWorldCoordinate(
    overrides.originX ?? component?.originX ?? (component?.width ?? 160) / 2,
  ),
  originY: formatWorldCoordinate(
    overrides.originY ?? component?.originY ?? (component?.height ?? 120) / 2,
  ),
  rotate: formatWorldCoordinate(overrides.rotate ?? component?.rotate ?? 0),
  opacity: formatWorldCoordinate(overrides.opacity ?? component?.opacity ?? 1),
  backgroundColor: String(
    overrides.backgroundColor ??
      component?.backgroundColor ??
      SHAPE_TOOL_DEFAULT_FILL,
  ),
  borderWidth: formatWorldCoordinate(
    overrides.borderWidth ?? component?.borderTopWidth ?? 0,
  ),
  borderColor: String(
    overrides.borderColor ?? component?.borderTopColor ?? "#000000",
  ),
  borderType: String(
    overrides.borderType ?? component?.borderTopType ?? "solid",
  ),
  shadowBlur: formatWorldCoordinate(
    overrides.shadowBlur ?? component?.shadowBlur ?? 0,
  ),
  shadowOffsetX: formatWorldCoordinate(
    overrides.shadowOffsetX ?? component?.shadowOffsetX ?? 0,
  ),
  shadowOffsetY: formatWorldCoordinate(
    overrides.shadowOffsetY ?? component?.shadowOffsetY ?? 0,
  ),
  shadowColor: String(
    overrides.shadowColor ?? component?.shadowColor ?? "#00000000",
  ),
  color: String(overrides.color ?? component?.color ?? SHAPE_TOOL_DEFAULT_TEXT),
  text: String(overrides.text ?? component?.text ?? ""),
  fontSize: formatWorldCoordinate(overrides.fontSize ?? component?.fontSize ?? 24),
  radiusTopLeft: formatWorldCoordinate(
    overrides.radiusTopLeft ?? component?.radiusTopLeft ?? 0,
  ),
  radiusTopRight: formatWorldCoordinate(
    overrides.radiusTopRight ?? component?.radiusTopRight ?? 0,
  ),
  radiusBottomLeft: formatWorldCoordinate(
    overrides.radiusBottomLeft ?? component?.radiusBottomLeft ?? 0,
  ),
  radiusBottomRight: formatWorldCoordinate(
    overrides.radiusBottomRight ?? component?.radiusBottomRight ?? 0,
  ),
});

const applyShapeDraftToComponent = (draft, component = {}) => {
  const width = Math.max(
    SHAPE_TOOL_MIN_SIZE,
    parseEditableNumber(draft?.width) ?? Number(component?.width ?? 160),
  );
  const height = Math.max(
    SHAPE_TOOL_MIN_SIZE,
    parseEditableNumber(draft?.height) ?? Number(component?.height ?? 120),
  );

  return {
    ...component,
    animationComponentId:
      component?.animationComponentId ?? draft?.shapeId ?? component?.componentId,
    animationComponentName:
      String(draft?.name || "").trim() ||
      component?.animationComponentName ||
      component?.componentName ||
      draft?.shapeId,
    visibility:
      String(draft?.visibility ?? component?.visibility ?? true) !== "false",
    shape: draft?.shape || component?.shape || "rect",
    x: parseEditableNumber(draft?.x) ?? Number(component?.x ?? 0),
    y: parseEditableNumber(draft?.y) ?? Number(component?.y ?? 0),
    z: parseEditableNumber(draft?.z) ?? Number(component?.z ?? 0),
    width,
    height,
    originX: Math.min(
      width,
      Math.max(
        0,
        parseEditableNumber(draft?.originX) ?? Number(component?.originX ?? width / 2),
      ),
    ),
    originY: Math.min(
      height,
      Math.max(
        0,
        parseEditableNumber(draft?.originY) ?? Number(component?.originY ?? height / 2),
      ),
    ),
    rotate: parseEditableNumber(draft?.rotate) ?? Number(component?.rotate ?? 0),
    opacity: Math.min(
      1,
      Math.max(
        0,
        parseEditableNumber(draft?.opacity) ?? Number(component?.opacity ?? 1),
      ),
    ),
    backgroundColor:
      String(draft?.backgroundColor || "").trim() ||
      component?.backgroundColor ||
      SHAPE_TOOL_DEFAULT_FILL,
    borderTopWidth:
      parseEditableNumber(draft?.borderWidth) ?? Number(component?.borderTopWidth ?? 0),
    borderRightWidth:
      parseEditableNumber(draft?.borderWidth) ??
      Number(component?.borderRightWidth ?? component?.borderTopWidth ?? 0),
    borderBottomWidth:
      parseEditableNumber(draft?.borderWidth) ??
      Number(component?.borderBottomWidth ?? component?.borderTopWidth ?? 0),
    borderLeftWidth:
      parseEditableNumber(draft?.borderWidth) ??
      Number(component?.borderLeftWidth ?? component?.borderTopWidth ?? 0),
    borderTopColor:
      String(draft?.borderColor || "").trim() ||
      component?.borderTopColor ||
      "#000000",
    borderRightColor:
      String(draft?.borderColor || "").trim() ||
      component?.borderRightColor ||
      component?.borderTopColor ||
      "#000000",
    borderBottomColor:
      String(draft?.borderColor || "").trim() ||
      component?.borderBottomColor ||
      component?.borderTopColor ||
      "#000000",
    borderLeftColor:
      String(draft?.borderColor || "").trim() ||
      component?.borderLeftColor ||
      component?.borderTopColor ||
      "#000000",
    borderTopType:
      String(draft?.borderType || "").trim() ||
      component?.borderTopType ||
      "solid",
    borderRightType:
      String(draft?.borderType || "").trim() ||
      component?.borderRightType ||
      component?.borderTopType ||
      "solid",
    borderBottomType:
      String(draft?.borderType || "").trim() ||
      component?.borderBottomType ||
      component?.borderTopType ||
      "solid",
    borderLeftType:
      String(draft?.borderType || "").trim() ||
      component?.borderLeftType ||
      component?.borderTopType ||
      "solid",
    shadowBlur:
      parseEditableNumber(draft?.shadowBlur) ?? Number(component?.shadowBlur ?? 0),
    shadowOffsetX:
      parseEditableNumber(draft?.shadowOffsetX) ??
      Number(component?.shadowOffsetX ?? 0),
    shadowOffsetY:
      parseEditableNumber(draft?.shadowOffsetY) ??
      Number(component?.shadowOffsetY ?? 0),
    shadowColor:
      String(draft?.shadowColor || "").trim() ||
      component?.shadowColor ||
      "#00000000",
    color:
      String(draft?.color || "").trim() ||
      component?.color ||
      SHAPE_TOOL_DEFAULT_TEXT,
    text: draft?.text ?? component?.text ?? "",
    fontSize:
      parseEditableNumber(draft?.fontSize) ?? Number(component?.fontSize ?? 24),
    radiusTopLeft:
      parseEditableNumber(draft?.radiusTopLeft) ??
      Number(component?.radiusTopLeft ?? 0),
    radiusTopRight:
      parseEditableNumber(draft?.radiusTopRight) ??
      Number(component?.radiusTopRight ?? 0),
    radiusBottomLeft:
      parseEditableNumber(draft?.radiusBottomLeft) ??
      Number(component?.radiusBottomLeft ?? 0),
    radiusBottomRight:
      parseEditableNumber(draft?.radiusBottomRight) ??
      Number(component?.radiusBottomRight ?? 0),
  };
};

const projectComponentRect = (component, metrics, cameraState) => {
  if (!metrics) return null;

  const componentWorldBottomLeftX = Number(component?.x || 0);
  const componentWorldBottomLeftY = Number(component?.y || 0);
  const componentHeight = Number(component?.height || 0);
  const componentWidth = Number(component?.width || 0);
  const componentOriginX = Number(component?.originX || 0);
  const componentOriginY = Number(component?.originY || 0);

  const componentWorldOriginX = componentWorldBottomLeftX + componentOriginX;
  const componentWorldOriginY = componentWorldBottomLeftY + componentOriginY;
  const dx = componentWorldOriginX - cameraState.x;
  const dy = componentWorldOriginY - cameraState.y;

  const screenOriginX = WORLD_CENTER_X + dx * cameraState.zoom;
  const screenOriginY = WORLD_HEIGHT - (WORLD_CENTER_Y + dy * cameraState.zoom);

  const worldX = screenOriginX - componentOriginX * cameraState.zoom;
  const worldY =
    screenOriginY - (componentHeight - componentOriginY) * cameraState.zoom;

  return {
    x: metrics.offsetX + worldX * metrics.scale,
    y: metrics.offsetY + worldY * metrics.scale,
    w: componentWidth * cameraState.zoom * metrics.scale,
    h: componentHeight * cameraState.zoom * metrics.scale,
  };
};

const buildShapeDslBlock = (draft, time, includeDeclaration = false) => {
  const name = String(draft?.name || "").trim();
  if (!name) return "";

  const lines = [];
  if (includeDeclaration) lines.push(`component|${name};`);
  lines.push(`time=${formatDslNumber(time)}{`);
  lines.push(
    `  ${String(draft?.visibility) === "false" ? "hide" : "show"}|${name};`,
  );
  lines.push(
    `  position|${name}|x=${formatDslNumber(draft?.x)}, y=${formatDslNumber(draft?.y)}, z=${formatDslNumber(draft?.z)};`,
  );
  lines.push(
    `  size|${name}|width=${formatDslNumber(draft?.width)}, height=${formatDslNumber(draft?.height)};`,
  );
  lines.push(
    `  origin|${name}|x=${formatDslNumber(draft?.originX)}, y=${formatDslNumber(draft?.originY)};`,
  );
  lines.push(`  shape|${name}|value="${escapeDslString(draft?.shape || "rect")}";`);
  lines.push(
    `  background|${name}|color="${escapeDslString(draft?.backgroundColor || SHAPE_TOOL_DEFAULT_FILL)}";`,
  );
  if ((parseEditableNumber(draft?.borderWidth) ?? 0) > 0) {
    const borderWidth = formatDslNumber(draft?.borderWidth ?? 0);
    const borderColor = escapeDslString(draft?.borderColor || "#000000");
    const borderType = escapeDslString(draft?.borderType || "solid");
    lines.push(
      `  borderTop|${name}|width=${borderWidth}, color="${borderColor}", type="${borderType}";`,
    );
    lines.push(
      `  borderRight|${name}|width=${borderWidth}, color="${borderColor}", type="${borderType}";`,
    );
    lines.push(
      `  borderBottom|${name}|width=${borderWidth}, color="${borderColor}", type="${borderType}";`,
    );
    lines.push(
      `  borderLeft|${name}|width=${borderWidth}, color="${borderColor}", type="${borderType}";`,
    );
  }
  if ((parseEditableNumber(draft?.shadowBlur) ?? 0) > 0) {
    lines.push(
      `  shadow|${name}|color="${escapeDslString(draft?.shadowColor || "#00000000")}", blur=${formatDslNumber(draft?.shadowBlur ?? 0)}, x=${formatDslNumber(draft?.shadowOffsetX ?? 0)}, y=${formatDslNumber(draft?.shadowOffsetY ?? 0)};`,
    );
  }
  lines.push(
    `  opacity|${name}|value=${formatDslNumber(draft?.opacity ?? 1)};`,
  );
  lines.push(
    `  rotation|${name}|value=${formatDslNumber(draft?.rotate ?? 0)};`,
  );
  lines.push(
    `  radius|${name}|tl=${formatDslNumber(draft?.radiusTopLeft ?? 0)}, tr=${formatDslNumber(draft?.radiusTopRight ?? 0)}, bl=${formatDslNumber(draft?.radiusBottomLeft ?? 0)}, br=${formatDslNumber(draft?.radiusBottomRight ?? 0)};`,
  );
  if (String(draft?.text || "").length) {
    lines.push(
      `  text|${name}|value="${escapeDslString(draft?.text)}", size=${formatDslNumber(draft?.fontSize ?? 24)}, color="${escapeDslString(draft?.color || SHAPE_TOOL_DEFAULT_TEXT)}";`,
    );
  }
  lines.push("}");
  return lines.join("\n");
};

const clampPreviewPan = (pan, zoom, width, height) => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { x: 0, y: 0 };
  }

  const minX = width - width * zoom;
  const minY = height - height * zoom;
  return {
    x: Math.min(0, Math.max(minX, Number(pan?.x) || 0)),
    y: Math.min(0, Math.max(minY, Number(pan?.y) || 0)),
  };
};

const CanvasEditorDisplay = ({
  animation,
  assets,
  currentScriptText = "",
  onInsertMarkerTime = () => false,
  onInsertScriptSnippet = () => false,
  location = { x: "", y: "" },
  showComponentBar = false,
  showTimelines = false,
  timeDisplayMode = "clock",
  timeLock = false,
  onTimeLockChange = () => {},
  lockedTimeRef,
  isLocationPicking = false,
  onLocationInputChange = () => {},
  onToggleLocationPicking = () => {},
  onLocationPick = () => {},
  onCancelLocationPick = () => {},
  toolCamera = {},
  isCameraOverrideActive = false,
  onCameraInputChange = () => {},
  onSyncCameraToLive = () => {},
  onResetCameraOverride = () => {},
  cameraOverride = null,
  onCameraStateChange = () => {},
}) => {
  const stageWrapperRef = useRef(null);
  const stageInnerRef = useRef(null);
  const canvasRef = useRef(null);
  const settingsRef = useRef(null);
  const toastTimerRef = useRef(null);
  const playbackStartRafRef = useRef(null);
  const prevAnimationRef = useRef(null);
  const customFrameInteractionRef = useRef(null);
  const shapeToolInteractionRef = useRef(null);
  const shapeToolCounterRef = useRef(1);
  const shapeClipboardRef = useRef([]);
  const lastShapeWorldClickRef = useRef(null);
  const localLockedTimeRef = useRef(0);
  const effectiveLockedTimeRef = lockedTimeRef ?? localLockedTimeRef;
  const hasExternalTimeLockControl = Boolean(lockedTimeRef);
  const lastInteractionInsideRef = useRef(false);

  const [showControls, setShowControls] = useState(true);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const [exportType, setExportType] = useState("video");
  const [exportQuality, setExportQuality] = useState("1080p");
  const [exportLayout, setExportLayout] = useState("landscape");
  const [exportStartTime, setExportStartTime] = useState("0");
  const [exportEndTime, setExportEndTime] = useState("0");
  const [exportImageTime, setExportImageTime] = useState("0");
  const [customExportFrame, setCustomExportFrame] = useState(
    createDefaultCustomFrame,
  );
  const [customFrameSnap, setCustomFrameSnap] = useState({
    x: false,
    y: false,
  });
  const [chapterToast, setChapterToast] = useState(null);
  const [scrubberHover, setScrubberHover] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [speed, setSpeedState] = useState(1);
  const [layoutTick, setLayoutTick] = useState(0);
  const [internalTimeLock, setInternalTimeLock] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [lockedMarkerId, setLockedMarkerId] = useState(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [segmentStartMarkerId, setSegmentStartMarkerId] = useState(null);
  const [segmentEndMarkerId, setSegmentEndMarkerId] = useState(null);
  const [isSegmentPreviewActive, setIsSegmentPreviewActive] = useState(false);
  const [locationPickHover, setLocationPickHover] = useState(null);
  const [activeToolTab, setActiveToolTab] = useState("waveform");
  const [shapeDrafts, setShapeDrafts] = useState([]);
  const [selectedShapeIds, setSelectedShapeIds] = useState([]);
  const markerCounterRef = useRef(1);
  const audioMarkersSaveTimerRef = useRef(null);
  const savedMarkersSnapshotRef = useRef("[]");
  const markerUndoStackRef = useRef([]);
  const markerRedoStackRef = useRef([]);

  const hasAnimationPayload = Boolean(animation);

  const initialComponents = useMemo(() => {
    const animationComponents = Array.isArray(animation?.animationComponents)
      ? animation.animationComponents
      : [];
    return animationComponents.map((ac) => ({
      ...ac.component,
      animationComponentId: ac.animationComponentId,
      animationComponentName: ac.animationComponentName,
      shape: ac.component?.shape || "rect",
    }));
  }, [animation]);

  const shapeDraftMap = useMemo(
    () => new Map(shapeDrafts.map((draft) => [draft.shapeId, draft])),
    [shapeDrafts],
  );

  const timelines = useMemo(
    () => (Array.isArray(animation?.timelines) ? animation.timelines : []),
    [animation],
  );
  const camera = useMemo(
    () => animation?.camera ?? {},
    [animation],
  );
  const cameraTimelines = useMemo(
    () => (Array.isArray(animation?.cameraTimelines) ? animation.cameraTimelines : []),
    [animation],
  );
  const connectors = useMemo(
    () => (Array.isArray(animation?.connectors) ? animation.connectors : []),
    [animation],
  );
  const chapters = useMemo(
    () => (Array.isArray(animation?.chapters) ? animation.chapters : []),
    [animation],
  );
  const audioTracks = useMemo(
    () => (Array.isArray(animation?.audioTracks) ? animation.audioTracks : []),
    [animation],
  );
  const persistedAudioMarkers = useMemo(
    () =>
      normalizeAudioMarkers(
        animation?.audioMarkers ?? animation?.metadata?.audioMarkers ?? [],
      ),
    [animation?.audioMarkers, animation?.metadata?.audioMarkers],
  );
  const animationId = animation?.metadata?.animationId || animation?.animationId;
  const persistedLockedPlaybackMarkerId =
    animation?.lockedPlaybackMarkerId ??
    animation?.metadata?.lockedPlaybackMarkerId ??
    null;

  const duration = animation?.metadata?.duration || 0;
  const parsedExportStartTime = parseTimeInput(exportStartTime);
  const parsedExportEndTime = parseTimeInput(exportEndTime);
  const parsedExportImageTime = parseTimeInput(exportImageTime);
  const normalizedCustomExportFrame = clampCustomFrame(customExportFrame);
  const exportLayoutOptions =
    exportType === "image"
      ? IMAGE_EXPORT_LAYOUT_OPTIONS
      : VIDEO_EXPORT_LAYOUT_OPTIONS;
  const requiresCustomFrame =
    exportLayout === "portrait_custom" ||
    (exportType === "image" && exportLayout === "custom_size");
  const exportRangeError =
    exportType === "image"
      ? parsedExportImageTime === null
        ? "Enter a valid image time"
        : parsedExportImageTime < 0
          ? "Time must be 0 or more"
          : parsedExportImageTime > duration
            ? "Time cannot exceed the animation duration"
            : null
      : parsedExportStartTime === null || parsedExportEndTime === null
        ? "Enter valid start and end times"
        : parsedExportStartTime < 0
          ? "Start time must be 0 or more"
          : parsedExportEndTime <= parsedExportStartTime
            ? "End time must be greater than start time"
            : parsedExportEndTime > duration
              ? "End time cannot exceed the animation duration"
              : null;
  const exportFrameError = requiresCustomFrame
    ? normalizedCustomExportFrame.width <= 0 ||
      normalizedCustomExportFrame.height <= 0
      ? "Custom frame must be valid"
      : null
    : null;
  const hasExportableDuration = exportType === "image" ? duration >= 0 : duration > 0;
  const canExport =
    Boolean(animation?.metadata?.animationId) &&
    hasExportableDuration &&
    !exportRangeError &&
    !exportFrameError;

  const {
    components,
    camera: animatedCamera,
    play,
    pause,
    reset,
    seek,
    isPlaying,
    currentTime,
    setSpeed,
  } = useAnimationEngine(
    initialComponents,
    timelines,
    camera,
    cameraTimelines,
    duration,
  );

  const previewComponents = useMemo(() => {
    const merged = components.map((component) => {
      const shapeId = component.animationComponentId;
      const draft = shapeDraftMap.get(shapeId);
      if (!draft) return component;
      return applyShapeDraftToComponent(draft, component);
    });

    const newShapes = shapeDrafts
      .filter((draft) => draft.isNew)
      .map((draft) => applyShapeDraftToComponent(draft));

    return [...merged, ...newShapes];
  }, [components, shapeDraftMap, shapeDrafts]);

  const previewComponentMap = useMemo(
    () =>
      new Map(
        previewComponents.map((component) => [component.animationComponentId, component]),
      ),
    [previewComponents],
  );

  const shapeToolItems = useMemo(() => {
    const sorted = [...previewComponents].sort((a, b) => {
      if ((a.z || 0) !== (b.z || 0)) return (b.z || 0) - (a.z || 0);
      return String(a.animationComponentName || "").localeCompare(
        String(b.animationComponentName || ""),
      );
    });

    return sorted.map((component) => {
      const draft = shapeDraftMap.get(component.animationComponentId);
      return {
        ...buildShapeDraftFromComponent(component, {
          shapeId: component.animationComponentId,
          isNew: Boolean(draft?.isNew),
        }),
        isDraft: Boolean(draft),
      };
    });
  }, [previewComponents, shapeDraftMap]);
  const primarySelectedShapeId =
    selectedShapeIds.length > 0 ? selectedShapeIds[selectedShapeIds.length - 1] : null;
  const animatedCameraX = Number(animatedCamera?.x ?? WORLD_CENTER_X);
  const animatedCameraY = Number(animatedCamera?.y ?? WORLD_CENTER_Y);
  const animatedCameraZ = Number(animatedCamera?.z ?? 0);
  const animatedCameraZoom = Math.max(Number(animatedCamera?.zoom ?? 1), 0.0001);
  const animatedCameraRotation = Number(animatedCamera?.rotation || 0);
  const animatedCameraOriginX = Number(animatedCamera?.originX ?? WORLD_CENTER_X);
  const animatedCameraOriginY = Number(animatedCamera?.originY ?? WORLD_CENTER_Y);
  const animatedCameraXVelocity = Number(animatedCamera?.xVelocity ?? 0);
  const animatedCameraYVelocity = Number(animatedCamera?.yVelocity ?? 0);
  const animatedCameraZVelocity = Number(animatedCamera?.zVelocity ?? 0);
  const animatedCameraXAcceleration = Number(animatedCamera?.xAcceleration ?? 0);
  const animatedCameraYAcceleration = Number(animatedCamera?.yAcceleration ?? 0);
  const animatedCameraZAcceleration = Number(animatedCamera?.zAcceleration ?? 0);
  const animatedCameraZoomVelocity = Number(animatedCamera?.zoomVelocity ?? 0);
  const animatedCameraZoomAcceleration = Number(animatedCamera?.zoomAcceleration ?? 0);
  const animatedCameraRotationVelocity = Number(animatedCamera?.rotationVelocity ?? 0);
  const animatedCameraRotationAcceleration = Number(animatedCamera?.rotationAcceleration ?? 0);
  const overrideAnchorTime = Number(cameraOverride?.time ?? currentTime);
  const overrideDeltaTime = currentTime - overrideAnchorTime;
  const resolvedCameraXVelocity = Number(
    cameraOverride?.xVelocity ?? animatedCameraXVelocity,
  );
  const resolvedCameraYVelocity = Number(
    cameraOverride?.yVelocity ?? animatedCameraYVelocity,
  );
  const resolvedCameraZVelocity = Number(
    cameraOverride?.zVelocity ?? animatedCameraZVelocity,
  );
  const resolvedCameraXAcceleration = Number(
    cameraOverride?.xAcceleration ?? animatedCameraXAcceleration,
  );
  const resolvedCameraYAcceleration = Number(
    cameraOverride?.yAcceleration ?? animatedCameraYAcceleration,
  );
  const resolvedCameraZAcceleration = Number(
    cameraOverride?.zAcceleration ?? animatedCameraZAcceleration,
  );
  const resolvedCameraZoomVelocity = Number(
    cameraOverride?.zoomVelocity ?? animatedCameraZoomVelocity,
  );
  const resolvedCameraZoomAcceleration = Number(
    cameraOverride?.zoomAcceleration ?? animatedCameraZoomAcceleration,
  );
  const resolvedCameraRotationVelocity = Number(
    cameraOverride?.rotationVelocity ?? animatedCameraRotationVelocity,
  );
  const resolvedCameraRotationAcceleration = Number(
    cameraOverride?.rotationAcceleration ?? animatedCameraRotationAcceleration,
  );
  const resolvedCameraX =
    Number(cameraOverride?.x ?? animatedCameraX) +
    resolvedCameraXVelocity * overrideDeltaTime +
    0.5 * resolvedCameraXAcceleration * overrideDeltaTime * overrideDeltaTime;
  const resolvedCameraY =
    Number(cameraOverride?.y ?? animatedCameraY) +
    resolvedCameraYVelocity * overrideDeltaTime +
    0.5 * resolvedCameraYAcceleration * overrideDeltaTime * overrideDeltaTime;
  const resolvedCameraZ =
    Number(cameraOverride?.z ?? animatedCameraZ) +
    resolvedCameraZVelocity * overrideDeltaTime +
    0.5 * resolvedCameraZAcceleration * overrideDeltaTime * overrideDeltaTime;
  const resolvedCameraZoom = Math.max(
    Number(cameraOverride?.zoom ?? animatedCameraZoom) +
      resolvedCameraZoomVelocity * overrideDeltaTime +
      0.5 *
        resolvedCameraZoomAcceleration *
        overrideDeltaTime *
        overrideDeltaTime,
    0.0001,
  );
  const resolvedCameraRotation =
    Number(cameraOverride?.rotation ?? animatedCameraRotation) +
    resolvedCameraRotationVelocity * overrideDeltaTime +
    0.5 *
      resolvedCameraRotationAcceleration *
      overrideDeltaTime *
      overrideDeltaTime;

  const audioEngine = useAudioEngine({
    audioTracks,
    assets,
    isPlaying,
    currentTime,
    duration,
  });

  const {
    exportMedia,
    cancelExport,
    isExporting,
    exportProgress,
    exportEtaSeconds,
    exportPhase,
    exportError,
  } = useAnimationExport({
    animationId,
    animationTitle: animation?.metadata?.title || animation?.title || "animation",
    exportType,
    quality: exportQuality,
    layout: exportLayout,
    startTime: parsedExportStartTime ?? 0,
    endTime: parsedExportEndTime ?? duration,
    time: parsedExportImageTime ?? 0,
    frame: requiresCustomFrame ? normalizedCustomExportFrame : null,
  });

  useEffect(() => {
    const nextSnapshot = buildAudioMarkersSnapshot(
      persistedAudioMarkers,
      persistedLockedPlaybackMarkerId,
    );
    savedMarkersSnapshotRef.current = nextSnapshot;
    setMarkers(persistedAudioMarkers);
    markerCounterRef.current = getNextMarkerCounter(persistedAudioMarkers);
    const nextLockedMarkerId = persistedAudioMarkers.some(
      (marker) => marker.markerId === persistedLockedPlaybackMarkerId,
    )
      ? persistedLockedPlaybackMarkerId
      : null;
    setLockedMarkerId(nextLockedMarkerId);
    setInternalTimeLock(Boolean(nextLockedMarkerId));
    setSelectedMarkerId(null);
    setSegmentStartMarkerId(null);
    setSegmentEndMarkerId(null);
    setIsSegmentPreviewActive(false);
    markerUndoStackRef.current = [];
    markerRedoStackRef.current = [];
  }, [persistedAudioMarkers, persistedLockedPlaybackMarkerId, animationId]);

  useEffect(() => {
    if (!lockedMarkerId) {
      setInternalTimeLock(false);
      return;
    }
    const lockedMarker = markers.find((marker) => marker.markerId === lockedMarkerId);
    if (!lockedMarker) {
      setLockedMarkerId(null);
      setInternalTimeLock(false);
      return;
    }
    effectiveLockedTimeRef.current = lockedMarker.time;
    setInternalTimeLock(true);
  }, [effectiveLockedTimeRef, lockedMarkerId, markers]);

  useEffect(() => {
    if (selectedMarkerId && !markers.some((marker) => marker.markerId === selectedMarkerId)) {
      setSelectedMarkerId(null);
    }
    if (
      segmentStartMarkerId &&
      !markers.some((marker) => marker.markerId === segmentStartMarkerId)
    ) {
      setSegmentStartMarkerId(null);
    }
    if (
      segmentEndMarkerId &&
      !markers.some((marker) => marker.markerId === segmentEndMarkerId)
    ) {
      setSegmentEndMarkerId(null);
    }
  }, [markers, selectedMarkerId, segmentStartMarkerId, segmentEndMarkerId]);

  const buildMarkerHistorySnapshot = useCallback(
    () => ({
      markers: buildAudioMarkersPayload(markers),
      lockedMarkerId,
      selectedMarkerId,
      segmentStartMarkerId,
      segmentEndMarkerId,
    }),
    [lockedMarkerId, markers, selectedMarkerId, segmentStartMarkerId, segmentEndMarkerId],
  );

  const applyMarkerHistorySnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    setMarkers(normalizeAudioMarkers(snapshot.markers || []));
    setLockedMarkerId(snapshot.lockedMarkerId || null);
    setSelectedMarkerId(snapshot.selectedMarkerId || null);
    setSegmentStartMarkerId(snapshot.segmentStartMarkerId || null);
    setSegmentEndMarkerId(snapshot.segmentEndMarkerId || null);
    setIsSegmentPreviewActive(false);
  }, []);

  const pushMarkerHistory = useCallback(() => {
    markerUndoStackRef.current.push(buildMarkerHistorySnapshot());
    if (markerUndoStackRef.current.length > 100) {
      markerUndoStackRef.current.shift();
    }
    markerRedoStackRef.current = [];
  }, [buildMarkerHistorySnapshot]);

  useEffect(() => {
    if (!animationId) return;

    const normalizedMarkers = buildAudioMarkersPayload(markers);
    const nextSnapshot = buildAudioMarkersSnapshot(normalizedMarkers, lockedMarkerId);
    if (nextSnapshot === savedMarkersSnapshotRef.current) {
      return;
    }

    if (audioMarkersSaveTimerRef.current) {
      clearTimeout(audioMarkersSaveTimerRef.current);
    }

    audioMarkersSaveTimerRef.current = window.setTimeout(async () => {
      try {
        const result = await updateAudioMarkers(
          animationId,
          normalizedMarkers,
          lockedMarkerId,
        );
        const savedMarkers = normalizeAudioMarkers(result?.audioMarkers || []);
        const savedLockedMarkerId = result?.lockedPlaybackMarkerId || null;
        savedMarkersSnapshotRef.current = buildAudioMarkersSnapshot(
          savedMarkers,
          savedLockedMarkerId,
        );
        markerCounterRef.current = getNextMarkerCounter(savedMarkers);
        setLockedMarkerId(savedLockedMarkerId);
      } catch (error) {
        console.error("Failed to save audio markers", error);
      }
    }, AUDIO_MARKER_SAVE_DELAY_MS);

    return () => {
      if (audioMarkersSaveTimerRef.current) {
        clearTimeout(audioMarkersSaveTimerRef.current);
        audioMarkersSaveTimerRef.current = null;
      }
    };
  }, [animationId, lockedMarkerId, markers]);

  useEffect(() => {
    onCameraStateChange({
      x: animatedCameraX,
      y: animatedCameraY,
      z: animatedCameraZ,
      zoom: animatedCameraZoom,
      rotation: animatedCameraRotation,
      originX: animatedCameraOriginX,
      originY: animatedCameraOriginY,
      xVelocity: animatedCameraXVelocity,
      yVelocity: animatedCameraYVelocity,
      zVelocity: animatedCameraZVelocity,
      xAcceleration: animatedCameraXAcceleration,
      yAcceleration: animatedCameraYAcceleration,
      zAcceleration: animatedCameraZAcceleration,
      zoomVelocity: animatedCameraZoomVelocity,
      zoomAcceleration: animatedCameraZoomAcceleration,
      rotationVelocity: animatedCameraRotationVelocity,
      rotationAcceleration: animatedCameraRotationAcceleration,
      time: currentTime,
    });
  }, [
    animatedCameraX,
    animatedCameraY,
    animatedCameraZ,
    animatedCameraZoom,
    animatedCameraRotation,
    animatedCameraOriginX,
    animatedCameraOriginY,
    animatedCameraXVelocity,
    animatedCameraYVelocity,
    animatedCameraZVelocity,
    animatedCameraXAcceleration,
    animatedCameraYAcceleration,
    animatedCameraZAcceleration,
    animatedCameraZoomVelocity,
    animatedCameraZoomAcceleration,
    animatedCameraRotationVelocity,
    animatedCameraRotationAcceleration,
    currentTime,
    onCameraStateChange,
  ]);

  const activeTimeLock = hasExternalTimeLockControl
    ? timeLock
    : internalTimeLock;

  useEffect(() => {
    return () => {
      if (playbackStartRafRef.current) {
        cancelAnimationFrame(playbackStartRafRef.current);
      }
      if (audioMarkersSaveTimerRef.current) {
        clearTimeout(audioMarkersSaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!activeTimeLock) return;
    if (prevAnimationRef.current === animation) return;
    prevAnimationRef.current = animation;

    seek(effectiveLockedTimeRef.current);
    audioEngine.seekAudio(effectiveLockedTimeRef.current);
  }, [animation, activeTimeLock, seek, audioEngine, effectiveLockedTimeRef]);

  const handleTimeLockChange = () => {
    if (hasExternalTimeLockControl) {
      onTimeLockChange((v) => !v);
      return;
    }
    if (!internalTimeLock) {
      effectiveLockedTimeRef.current = currentTime;
    }
    setInternalTimeLock((v) => !v);
  };

  const addMarker = useCallback(
    (time) => {
      if (!duration) return;
      pushMarkerHistory();
      const safeTime = Math.max(0, Math.min(duration, Number(time) || 0));
      const nextMarkerId = `marker_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const label = `M${markerCounterRef.current++}`;
      setMarkers((prev) =>
        [...prev, {
          markerId: nextMarkerId,
          time: safeTime,
          label,
        }].sort((a, b) => a.time - b.time),
      );
      setSelectedMarkerId(nextMarkerId);
    },
    [duration, pushMarkerHistory],
  );

  const updateMarker = useCallback((markerId, nextFields = {}, options = {}) => {
    if (!options.skipHistory) {
      pushMarkerHistory();
    }
    setMarkers((prev) =>
      prev
        .map((marker) =>
          marker.markerId === markerId
            ? {
                ...marker,
                ...nextFields,
                time:
                  nextFields.time == null
                    ? marker.time
                    : Math.max(0, Math.min(duration, Number(nextFields.time) || 0)),
              }
            : marker,
        )
        .sort((a, b) => a.time - b.time),
    );
  }, [duration, pushMarkerHistory]);

  const deleteMarker = useCallback((markerId) => {
    pushMarkerHistory();
    setLockedMarkerId((current) => (current === markerId ? null : current));
    setMarkers((prev) => prev.filter((marker) => marker.markerId !== markerId));
    setSelectedMarkerId((current) => (current === markerId ? null : current));
  }, [pushMarkerHistory]);

  const toggleMarkerLock = useCallback(
    (markerId) => {
      pushMarkerHistory();
      setLockedMarkerId((current) => {
        if (current === markerId) {
          setInternalTimeLock(false);
          return null;
        }
        const marker = markers.find((item) => item.markerId === markerId);
        if (marker) {
          effectiveLockedTimeRef.current = marker.time;
          setInternalTimeLock(true);
          seek(marker.time);
          audioEngine.seekAudio(marker.time);
        }
        return markerId;
      });
    },
    [audioEngine, effectiveLockedTimeRef, markers, pushMarkerHistory, seek],
  );

  const replaceMarkers = useCallback(
    (nextMarkers = []) => {
      pushMarkerHistory();
      const normalized = buildAudioMarkersPayload(nextMarkers);
      setMarkers(normalized);
      markerCounterRef.current = getNextMarkerCounter(normalized);
      setSelectedMarkerId(normalized[0]?.markerId || null);
      setLockedMarkerId((current) =>
        normalized.some((marker) => marker.markerId === current) ? current : null,
      );
      setSegmentStartMarkerId((current) =>
        normalized.some((marker) => marker.markerId === current) ? current : null,
      );
      setSegmentEndMarkerId((current) =>
        normalized.some((marker) => marker.markerId === current) ? current : null,
      );
      setIsSegmentPreviewActive(false);
    },
    [pushMarkerHistory],
  );

  const selectMarker = useCallback(
    (markerId, shouldSeek = true) => {
      setSelectedMarkerId(markerId);
      const marker = markers.find((item) => item.markerId === markerId);
      if (marker && shouldSeek) {
        seek(marker.time);
        audioEngine.seekAudio(marker.time);
      }
    },
    [audioEngine, markers, seek],
  );

  const beginMarkerDrag = useCallback(() => {
    pushMarkerHistory();
  }, [pushMarkerHistory]);

  const insertMarkerTime = useCallback(
    (markerId) => {
      const marker = markers.find((item) => item.markerId === markerId);
      if (!marker) return false;
      return onInsertMarkerTime(`${Number(marker.time || 0).toFixed(3)}`);
    },
    [markers, onInsertMarkerTime],
  );

  const playAtTime = useCallback(
    (time) => {
      const safeTime = Math.max(0, Math.min(duration, Number(time) || 0));
      if (playbackStartRafRef.current) {
        cancelAnimationFrame(playbackStartRafRef.current);
      }
      pause();
      seek(safeTime);
      audioEngine.seekAudio(safeTime);
      playbackStartRafRef.current = requestAnimationFrame(() => {
        playbackStartRafRef.current = null;
        play();
      });
      return true;
    },
    [audioEngine, duration, pause, play, seek],
  );

  const playFromMarker = useCallback(
    (markerId) => {
      const marker = markers.find((item) => item.markerId === markerId);
      if (!marker) return false;
      return playAtTime(marker.time);
    },
    [markers, playAtTime],
  );

  const setSegmentBoundary = useCallback(
    (boundary, markerId) => {
      pushMarkerHistory();
      if (boundary === "start") {
        setSegmentStartMarkerId(markerId);
      } else {
        setSegmentEndMarkerId(markerId);
      }
      setSelectedMarkerId(markerId);
      setIsSegmentPreviewActive(false);
    },
    [pushMarkerHistory],
  );

  const clearSegmentPreview = useCallback(() => {
    pushMarkerHistory();
    setSegmentStartMarkerId(null);
    setSegmentEndMarkerId(null);
    setIsSegmentPreviewActive(false);
  }, [pushMarkerHistory]);

  const playSegmentPreview = useCallback(() => {
    const startMarker = markers.find((item) => item.markerId === segmentStartMarkerId);
    const endMarker = markers.find((item) => item.markerId === segmentEndMarkerId);
    if (!startMarker || !endMarker) return false;
    const [startTime, endTime] =
      startMarker.time <= endMarker.time
        ? [startMarker.time, endMarker.time]
        : [endMarker.time, startMarker.time];
    setIsSegmentPreviewActive(true);
    return playAtTime(startTime);
  }, [markers, playAtTime, segmentEndMarkerId, segmentStartMarkerId]);

  const undoMarkerChange = useCallback(() => {
    const snapshot = markerUndoStackRef.current.pop();
    if (!snapshot) return false;
    markerRedoStackRef.current.push(buildMarkerHistorySnapshot());
    applyMarkerHistorySnapshot(snapshot);
    return true;
  }, [applyMarkerHistorySnapshot, buildMarkerHistorySnapshot]);

  const redoMarkerChange = useCallback(() => {
    const snapshot = markerRedoStackRef.current.pop();
    if (!snapshot) return false;
    markerUndoStackRef.current.push(buildMarkerHistorySnapshot());
    applyMarkerHistorySnapshot(snapshot);
    return true;
  }, [applyMarkerHistorySnapshot, buildMarkerHistorySnapshot]);

  useEffect(() => {
    if (!isSegmentPreviewActive || !isPlaying) return;
    const startMarker = markers.find((item) => item.markerId === segmentStartMarkerId);
    const endMarker = markers.find((item) => item.markerId === segmentEndMarkerId);
    if (!startMarker || !endMarker) {
      setIsSegmentPreviewActive(false);
      return;
    }
    const segmentEnd = Math.max(startMarker.time, endMarker.time);
    if (currentTime >= segmentEnd) {
      pause();
      seek(segmentEnd);
      audioEngine.seekAudio(segmentEnd);
      setIsSegmentPreviewActive(false);
    }
  }, [
    audioEngine,
    currentTime,
    isPlaying,
    isSegmentPreviewActive,
    markers,
    pause,
    seek,
    segmentEndMarkerId,
    segmentStartMarkerId,
  ]);

  const formatSeconds = (value) => `${Number(value || 0).toFixed(2)}s`;
  const getDisplayTime = (value) =>
    timeDisplayMode === "seconds" ? formatSeconds(value) : formatTime(value);
  const formatEta = (seconds) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return "estimating";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, "0")} left`;
  };
  const exportToolbarLabel = isExporting
    ? `${exportProgress}% · ${formatEta(exportEtaSeconds)}`
    : null;
  const exportToolbarPhase =
    isExporting && exportPhase
      ? exportPhase.replace(/_/g, " ")
      : null;

  const getCurrentChapter = useCallback(
    (time) => {
      if (!chapters.length) return null;
      let current = null;
      for (const c of chapters) {
        if (time >= c.time) current = c;
        else break;
      }
      return current;
    },
    [chapters],
  );

  const showChapterToast = useCallback((title) => {
    setChapterToast(title);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setChapterToast(null), 2000);
  }, []);

  const handleSetSpeed = (s) => {
    setSpeedState(s);
    setSpeed(s);
  };

  const applyCurrentTimeToExportBound = useCallback(
    (bound) => {
      const timeValue = currentTime.toFixed(2);
      if (bound === "start") setExportStartTime(timeValue);
      else setExportEndTime(timeValue);
    },
    [currentTime],
  );

  const resetExportRange = useCallback(() => {
    setExportStartTime("0");
    setExportEndTime(duration > 0 ? duration.toFixed(2) : "0");
  }, [duration]);

  const resetExportImageTime = useCallback(() => {
    setExportImageTime(currentTime.toFixed(2));
  }, [currentTime]);

  const resetCustomExportFrame = useCallback(() => {
    setCustomExportFrame(createDefaultCustomFrame());
  }, []);

  const handleStageWheelZoom = useCallback((event) => {
    if (requiresCustomFrame || isLocationPicking) return;

    const host = stageInnerRef.current;
    if (!host) return;

    const rect = host.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    event.preventDefault();

    const delta = event.deltaY < 0 ? 1.12 : 1 / 1.12;
    const nextZoom = Math.min(
      MAX_PREVIEW_ZOOM,
      Math.max(MIN_PREVIEW_ZOOM, previewZoom * delta),
    );
    if (Math.abs(nextZoom - previewZoom) < 0.0001) return;

    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const contentX = (localX - previewPan.x) / previewZoom;
    const contentY = (localY - previewPan.y) / previewZoom;
    const nextPan = clampPreviewPan(
      {
        x: localX - contentX * nextZoom,
        y: localY - contentY * nextZoom,
      },
      nextZoom,
      rect.width,
      rect.height,
    );

    setPreviewZoom(nextZoom);
    setPreviewPan(nextPan);
  }, [isLocationPicking, previewPan.x, previewPan.y, previewZoom, requiresCustomFrame]);

  const getStageMetrics = useCallback(() => {
    const host = stageInnerRef.current;
    if (!host) return null;

    const rect = host.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const scale = Math.min(rect.width / WORLD_WIDTH, rect.height / WORLD_HEIGHT);
    if (!scale) return null;

    return {
      rect,
      scale,
      offsetX: (rect.width - WORLD_WIDTH * scale) / 2,
      offsetY: (rect.height - WORLD_HEIGHT * scale) / 2,
      centerX: WORLD_CENTER_X,
      centerY: WORLD_CENTER_Y,
      previewZoom,
      previewPanX: previewPan.x,
      previewPanY: previewPan.y,
    };
  }, [previewPan.x, previewPan.y, previewZoom]);

  const getStagePointFromClient = useCallback((clientX, clientY) => {
    const host = stageInnerRef.current;
    if (!host) return null;
    const rect = host.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const localX = (clientX - rect.left - previewPan.x) / previewZoom;
    const localY = (clientY - rect.top - previewPan.y) / previewZoom;
    return {
      x: (localX / rect.width) * WORLD_WIDTH,
      y: (localY / rect.height) * WORLD_HEIGHT,
    };
  }, [previewPan.x, previewPan.y, previewZoom]);

  const getWorldPointFromClient = useCallback(
    (clientX, clientY) => {
      const metrics = getStageMetrics();
      if (!metrics) return null;

      const {
        rect,
        scale,
        offsetX,
        offsetY,
        centerX,
        centerY,
      } = metrics;

      const localPoint = {
        x: (clientX - rect.left - previewPan.x) / previewZoom,
        y: (clientY - rect.top - previewPan.y) / previewZoom,
      };
      const rotatedPoint = rotatePointAround(
        localPoint,
        {
          x: offsetX + centerX * scale,
          y: offsetY + centerY * scale,
        },
        resolvedCameraRotation,
      );
      const stageX = (rotatedPoint.x - offsetX) / scale;
      const stageY = (rotatedPoint.y - offsetY) / scale;

      if (
        !Number.isFinite(stageX) ||
        !Number.isFinite(stageY) ||
        stageX < 0 ||
        stageX > WORLD_WIDTH ||
        stageY < 0 ||
        stageY > WORLD_HEIGHT
      ) {
        return null;
      }

      return {
        x: clampWorldCoordinate(
          resolvedCameraX + (stageX - WORLD_CENTER_X) / resolvedCameraZoom,
          WORLD_WIDTH,
        ),
        y: clampWorldCoordinate(
          resolvedCameraY + (WORLD_CENTER_Y - stageY) / resolvedCameraZoom,
          WORLD_HEIGHT,
        ),
        time: currentTime,
      };
    },
    [
      currentTime,
      getStageMetrics,
      resolvedCameraRotation,
      resolvedCameraX,
      resolvedCameraY,
      resolvedCameraZoom,
      previewPan.x,
      previewPan.y,
      previewZoom,
    ],
  );

  useEffect(() => {
    setShapeDrafts([]);
    setSelectedShapeIds([]);
    shapeToolCounterRef.current = 1;
    shapeClipboardRef.current = [];
    lastShapeWorldClickRef.current = null;
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
  }, [animationId]);

  useEffect(() => {
    const host = stageInnerRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    setPreviewPan((prev) => clampPreviewPan(prev, previewZoom, rect.width, rect.height));
  }, [layoutTick, previewZoom]);

  const ensureShapeDraft = useCallback(
    (shapeId) => {
      const existingDraft = shapeDraftMap.get(shapeId);
      if (existingDraft) return existingDraft;

      const component = previewComponentMap.get(shapeId);
      if (!component) return null;

      const nextDraft = buildShapeDraftFromComponent(component, { shapeId });
      setShapeDrafts((prev) => [...prev, nextDraft]);
      return nextDraft;
    },
    [previewComponentMap, shapeDraftMap],
  );

  const updateShapeDraft = useCallback((shapeId, patch) => {
    setShapeDrafts((prev) =>
      prev.map((draft) =>
        draft.shapeId === shapeId ? { ...draft, ...patch } : draft,
      ),
    );
  }, []);

  const handleSelectShape = useCallback(
    (shapeId, options = {}) => {
      ensureShapeDraft(shapeId);
      setSelectedShapeIds((prev) => {
        if (options.toggle) {
          if (prev.includes(shapeId)) {
            return prev.filter((id) => id !== shapeId);
          }
          return [...prev, shapeId];
        }
        if (prev.length === 1 && prev[0] === shapeId) return prev;
        return [...prev.filter((id) => id !== shapeId), shapeId];
      });
      setActiveToolTab("shapes");
    },
    [ensureShapeDraft],
  );

  const handleClearSelectedShape = useCallback(() => {
    setSelectedShapeIds([]);
  }, []);

  const handleShapeOverlayBackgroundMouseDown = useCallback(
    (event) => {
      const point = getWorldPointFromClient(event.clientX, event.clientY);
      if (point) {
        lastShapeWorldClickRef.current = { x: point.x, y: point.y };
      }
      handleClearSelectedShape();
    },
    [getWorldPointFromClient, handleClearSelectedShape],
  );

  const handleAddShape = useCallback(
    (shapeType = "rect") => {
      const existingNames = new Set(
        previewComponents.map((component) =>
          String(component.animationComponentName || "").trim().toLowerCase(),
        ),
      );
      let name = `${shapeType}${shapeToolCounterRef.current}`;
      while (existingNames.has(name.toLowerCase())) {
        shapeToolCounterRef.current += 1;
        name = `${shapeType}${shapeToolCounterRef.current}`;
      }
      shapeToolCounterRef.current += 1;

      const shapeId = `draft-shape-${Date.now()}-${shapeToolCounterRef.current}`;
      const nextDraft = buildShapeDraftFromComponent(
        {
          animationComponentId: shapeId,
          animationComponentName: name,
        },
        {
          shapeId,
          isNew: true,
          name,
          shape: shapeType,
          x: 640,
          y: 320,
          z: 1,
          width: shapeType === "circle" ? 140 : 180,
          height: 140,
          originX: shapeType === "circle" ? 70 : 90,
          originY: 70,
          backgroundColor: SHAPE_TOOL_DEFAULT_FILL,
          color: SHAPE_TOOL_DEFAULT_TEXT,
          opacity: 1,
        },
      );

      setShapeDrafts((prev) => [...prev, nextDraft]);
      setSelectedShapeIds([shapeId]);
      setActiveToolTab("shapes");
    },
    [previewComponents],
  );

  const createUniqueShapeName = useCallback(
    (baseName, existingNames) => {
      const trimmedBase = String(baseName || "shape").trim() || "shape";
      let nextName = `${trimmedBase}_copy`;
      let suffix = 2;
      while (existingNames.has(nextName.toLowerCase())) {
        nextName = `${trimmedBase}_copy${suffix}`;
        suffix += 1;
      }
      existingNames.add(nextName.toLowerCase());
      return nextName;
    },
    [],
  );

  const handleCopySelectedShapes = useCallback(() => {
    const items = shapeToolItems.filter((shape) => selectedShapeIds.includes(shape.shapeId));
    if (!items.length) return;
    shapeClipboardRef.current = items.map((shape) => ({ ...shape }));
  }, [selectedShapeIds, shapeToolItems]);

  const handlePasteShapes = useCallback(() => {
    const copied = shapeClipboardRef.current;
    if (!copied.length) return;

    const anchorX = Math.min(...copied.map((shape) => parseEditableNumber(shape.x) ?? 0));
    const anchorY = Math.min(...copied.map((shape) => parseEditableNumber(shape.y) ?? 0));
    const target = lastShapeWorldClickRef.current ?? { x: anchorX + 24, y: anchorY + 24 };
    const offsetX = target.x - anchorX;
    const offsetY = target.y - anchorY;
    const existingNames = new Set(
      shapeToolItems.map((shape) => String(shape.name || "").trim().toLowerCase()),
    );

    const nextDrafts = copied.map((shape) => {
      const nextShapeId = `draft-shape-${Date.now()}-${shapeToolCounterRef.current++}`;
      return {
        ...shape,
        shapeId: nextShapeId,
        isNew: true,
        isDraft: true,
        name: createUniqueShapeName(shape.name || "shape", existingNames),
        x: formatWorldCoordinate((parseEditableNumber(shape.x) ?? 0) + offsetX),
        y: formatWorldCoordinate((parseEditableNumber(shape.y) ?? 0) + offsetY),
      };
    });

    setShapeDrafts((prev) => [...prev, ...nextDrafts]);
    setSelectedShapeIds(nextDrafts.map((shape) => shape.shapeId));
    setActiveToolTab("shapes");
  }, [createUniqueShapeName, shapeToolItems]);

  const handleCloneShape = useCallback(
    (shapeIdsInput) => {
      const ids = Array.isArray(shapeIdsInput)
        ? shapeIdsInput
        : shapeIdsInput
          ? [shapeIdsInput]
          : selectedShapeIds;
      const sourceShapes = shapeToolItems.filter((shape) => ids.includes(shape.shapeId));
      if (!sourceShapes.length) return;

      shapeClipboardRef.current = sourceShapes.map((shape) => ({ ...shape }));
      if (!lastShapeWorldClickRef.current) {
        lastShapeWorldClickRef.current = {
          x:
            Math.min(...sourceShapes.map((shape) => parseEditableNumber(shape.x) ?? 0)) +
            24,
          y:
            Math.min(...sourceShapes.map((shape) => parseEditableNumber(shape.y) ?? 0)) +
            24,
        };
      }
      handlePasteShapes();
    },
    [handlePasteShapes, selectedShapeIds, shapeToolItems],
  );

  const handleDeleteShape = useCallback((shapeIdsInput) => {
    const ids = new Set(
      Array.isArray(shapeIdsInput)
        ? shapeIdsInput
        : shapeIdsInput
          ? [shapeIdsInput]
          : selectedShapeIds,
    );
    if (!ids.size) return;
    setShapeDrafts((prev) => prev.filter((draft) => !ids.has(draft.shapeId)));
    setSelectedShapeIds((prev) => prev.filter((id) => !ids.has(id)));
  }, [selectedShapeIds]);

  const handleResetShape = useCallback((shapeIdsInput) => {
    const ids = new Set(
      Array.isArray(shapeIdsInput)
        ? shapeIdsInput
        : shapeIdsInput
          ? [shapeIdsInput]
          : selectedShapeIds,
    );
    if (!ids.size) return;
    setShapeDrafts((prev) => prev.filter((draft) => !ids.has(draft.shapeId)));
  }, [selectedShapeIds]);

  const handleSelectAllShapes = useCallback(() => {
    const ids = shapeToolItems.map((shape) => shape.shapeId);
    if (!ids.length) return;
    ids.forEach((id) => ensureShapeDraft(id));
    setSelectedShapeIds(ids);
    setActiveToolTab("shapes");
  }, [ensureShapeDraft, shapeToolItems]);

  const handleToggleSelectedShapeVisibility = useCallback(() => {
    if (!selectedShapeIds.length) return;
    const selectedItems = shapeToolItems.filter((shape) =>
      selectedShapeIds.includes(shape.shapeId),
    );
    if (!selectedItems.length) return;
    const shouldHide = selectedItems.some(
      (shape) => String(shape.visibility ?? "true") !== "false",
    );
    selectedItems.forEach((shape) => {
      const existingDraft = ensureShapeDraft(shape.shapeId);
      if (!existingDraft) return;
      updateShapeDraft(shape.shapeId, {
        visibility: String(!shouldHide ? true : false),
      });
    });
  }, [ensureShapeDraft, selectedShapeIds, shapeToolItems, updateShapeDraft]);

  const handleShapeFieldChange = useCallback(
    (shapeId, field, value) => {
      const existingDraft = ensureShapeDraft(shapeId);
      if (!existingDraft) return;
      updateShapeDraft(shapeId, { [field]: value });
    },
    [ensureShapeDraft, updateShapeDraft],
  );

  const buildSelectedShapeDsl = useCallback(
    (shapeIdsInput) => {
      const ids = Array.isArray(shapeIdsInput)
        ? shapeIdsInput
        : shapeIdsInput
          ? [shapeIdsInput]
          : selectedShapeIds;
      return ids
        .map((shapeId) => {
          const draft = shapeDraftMap.get(shapeId);
          if (!draft) return "";
          return buildShapeDslBlock(draft, currentTime, Boolean(draft.isNew));
        })
        .filter(Boolean)
        .join("\n\n");
    },
    [currentTime, selectedShapeIds, shapeDraftMap],
  );

  const buildAllShapesDsl = useCallback(
    () =>
      shapeDrafts
        .map((draft) => buildShapeDslBlock(draft, currentTime, Boolean(draft.isNew)))
        .filter(Boolean)
        .join("\n\n"),
    [currentTime, shapeDrafts],
  );

  const updateShapeInteractionFromPointer = useCallback(
    (clientX, clientY, preserveAspectRatio = false) => {
      const interaction = shapeToolInteractionRef.current;
      if (!interaction) return;

      const worldPoint = getWorldPointFromClient(clientX, clientY);
      if (!worldPoint) return;

      const dx = worldPoint.x - interaction.startWorld.x;
      const dy = worldPoint.y - interaction.startWorld.y;
      const next = { ...interaction.snapshot };

      if (interaction.mode === "move") {
        next.x = formatWorldCoordinate(interaction.snapshot.x + dx);
        next.y = formatWorldCoordinate(interaction.snapshot.y + dy);
      } else {
        let left = interaction.snapshot.x;
        let right = interaction.snapshot.x + interaction.snapshot.width;
        let bottom = interaction.snapshot.y;
        let top = interaction.snapshot.y + interaction.snapshot.height;

        if (preserveAspectRatio) {
          const aspectRatio = Math.max(
            0.0001,
            interaction.snapshot.width / Math.max(0.0001, interaction.snapshot.height),
          );
          const signX = interaction.mode.includes("w") ? -1 : 1;
          const signY = interaction.mode.includes("s") ? -1 : 1;
          const anchorX =
            signX < 0
              ? interaction.snapshot.x + interaction.snapshot.width
              : interaction.snapshot.x;
          const anchorY =
            signY < 0
              ? interaction.snapshot.y + interaction.snapshot.height
              : interaction.snapshot.y;

          let width = Math.abs(worldPoint.x - anchorX);
          let height = Math.abs(worldPoint.y - anchorY);

          if (width / aspectRatio >= height) {
            height = width / aspectRatio;
          } else {
            width = height * aspectRatio;
          }

          width = Math.max(SHAPE_TOOL_MIN_SIZE, width);
          height = Math.max(SHAPE_TOOL_MIN_SIZE, height);

          if (signX < 0) {
            left = anchorX - width;
            right = anchorX;
          } else {
            left = anchorX;
            right = anchorX + width;
          }

          if (signY < 0) {
            bottom = anchorY - height;
            top = anchorY;
          } else {
            bottom = anchorY;
            top = anchorY + height;
          }
        } else {
          if (interaction.mode.includes("w")) left += dx;
          if (interaction.mode.includes("e")) right += dx;
          if (interaction.mode.includes("s")) bottom += dy;
          if (interaction.mode.includes("n")) top += dy;

          if (right - left < SHAPE_TOOL_MIN_SIZE) {
            if (interaction.mode.includes("w")) left = right - SHAPE_TOOL_MIN_SIZE;
            else right = left + SHAPE_TOOL_MIN_SIZE;
          }
          if (top - bottom < SHAPE_TOOL_MIN_SIZE) {
            if (interaction.mode.includes("s")) bottom = top - SHAPE_TOOL_MIN_SIZE;
            else top = bottom + SHAPE_TOOL_MIN_SIZE;
          }
        }

        const width = right - left;
        const height = top - bottom;
        const originX = Math.min(
          width,
          Math.max(0, interaction.snapshot.originX),
        );
        const originY = Math.min(
          height,
          Math.max(0, interaction.snapshot.originY),
        );

        next.x = formatWorldCoordinate(left);
        next.y = formatWorldCoordinate(bottom);
        next.width = formatWorldCoordinate(width);
        next.height = formatWorldCoordinate(height);
        next.originX = formatWorldCoordinate(originX);
        next.originY = formatWorldCoordinate(originY);
      }

      updateShapeDraft(interaction.shapeId, next);
    },
    [getWorldPointFromClient, updateShapeDraft],
  );

  const stopShapeInteraction = useCallback(() => {
    shapeToolInteractionRef.current = null;
    window.removeEventListener("mousemove", handleShapeInteractionMouseMove);
    window.removeEventListener("mouseup", stopShapeInteraction);
  }, []);

  const handleShapeInteractionMouseMove = useCallback(
    (event) => {
      updateShapeInteractionFromPointer(
        event.clientX,
        event.clientY,
        Boolean(event.shiftKey),
      );
    },
    [updateShapeInteractionFromPointer],
  );

  const startShapeInteraction = useCallback(
    (event, shapeId, mode) => {
      event.preventDefault();
      event.stopPropagation();

      const draft = ensureShapeDraft(shapeId);
      const component = previewComponentMap.get(shapeId);
      if (!draft && !component) return;

      const worldPoint = getWorldPointFromClient(event.clientX, event.clientY);
      if (!worldPoint) return;
      lastShapeWorldClickRef.current = { x: worldPoint.x, y: worldPoint.y };

      const resolved = applyShapeDraftToComponent(draft ?? {}, component);
      shapeToolInteractionRef.current = {
        shapeId,
        mode,
        startWorld: worldPoint,
        snapshot: {
          x: Number(resolved.x || 0),
          y: Number(resolved.y || 0),
          width: Number(resolved.width || SHAPE_TOOL_MIN_SIZE),
          height: Number(resolved.height || SHAPE_TOOL_MIN_SIZE),
          originX: Number(resolved.originX || 0),
          originY: Number(resolved.originY || 0),
        },
      };

      setSelectedShapeIds((prev) =>
        prev.includes(shapeId) ? prev : [...prev.filter((id) => id !== shapeId), shapeId],
      );
      setActiveToolTab("shapes");
      window.addEventListener("mousemove", handleShapeInteractionMouseMove);
      window.addEventListener("mouseup", stopShapeInteraction);
    },
    [
      ensureShapeDraft,
      getWorldPointFromClient,
      handleShapeInteractionMouseMove,
      previewComponentMap,
      stopShapeInteraction,
    ],
  );

  useEffect(
    () => () => {
      window.removeEventListener("mousemove", handleShapeInteractionMouseMove);
      window.removeEventListener("mouseup", stopShapeInteraction);
    },
    [handleShapeInteractionMouseMove, stopShapeInteraction],
  );

  const shapeOverlayMetrics = useMemo(() => {
    const host = stageInnerRef.current;
    if (!host) return null;
    const rect = host.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const scale = Math.min(rect.width / WORLD_WIDTH, rect.height / WORLD_HEIGHT);
    return {
      width: rect.width,
      height: rect.height,
      scale,
      offsetX: (rect.width - WORLD_WIDTH * scale) / 2,
      offsetY: (rect.height - WORLD_HEIGHT * scale) / 2,
    };
  }, [layoutTick, currentTime, activeToolTab]);

  const shapeOverlayItems = useMemo(() => {
    if (!shapeOverlayMetrics) return [];
    const cameraState = {
      x: resolvedCameraX,
      y: resolvedCameraY,
      zoom: resolvedCameraZoom,
    };

    return [...previewComponents]
      .filter((component) => component.visibility)
      .sort((a, b) => {
        if ((a.z || 0) !== (b.z || 0)) return (a.z || 0) - (b.z || 0);
        return String(a.animationComponentId || "").localeCompare(
          String(b.animationComponentId || ""),
        );
      })
      .map((component) => {
        const rect = projectComponentRect(component, shapeOverlayMetrics, cameraState);
        if (!rect) return null;
        return {
          shapeId: component.animationComponentId,
          name: component.animationComponentName || component.animationComponentId,
          x: previewPan.x + rect.x * previewZoom,
          y: previewPan.y + rect.y * previewZoom,
          w: rect.w * previewZoom,
          h: rect.h * previewZoom,
          rotate: Number(component.rotate || 0),
        };
      })
      .filter(Boolean);
  }, [
    previewComponents,
    previewPan.x,
    previewPan.y,
    previewZoom,
    resolvedCameraX,
    resolvedCameraY,
    resolvedCameraZoom,
    shapeOverlayMetrics,
  ]);

  const updateCustomFrameFromPointer = useCallback(
    (clientX, clientY) => {
      const interaction = customFrameInteractionRef.current;
      if (!interaction) return;
      const point = getStagePointFromClient(clientX, clientY);
      if (!point) return;

      const dx = point.x - interaction.startPoint.x;
      const dy = point.y - interaction.startPoint.y;
      const initial = interaction.initialFrame;

      const applyCenterSnap = (candidateFrame) => {
        const next = clampCustomFrame(candidateFrame);
        const centerX = next.x + next.width / 2;
        const centerY = next.y + next.height / 2;

        if (Math.abs(centerX - WORLD_WIDTH / 2) <= CUSTOM_FRAME_SNAP_THRESHOLD) {
          next.x = WORLD_WIDTH / 2 - next.width / 2;
        }
        if (Math.abs(centerY - WORLD_HEIGHT / 2) <= CUSTOM_FRAME_SNAP_THRESHOLD) {
          next.y = WORLD_HEIGHT / 2 - next.height / 2;
        }

        const finalFrame = clampCustomFrame(next);
        const finalCenterX = finalFrame.x + finalFrame.width / 2;
        const finalCenterY = finalFrame.y + finalFrame.height / 2;
        setCustomFrameSnap({
          x: Math.abs(finalCenterX - WORLD_WIDTH / 2) < 0.001,
          y: Math.abs(finalCenterY - WORLD_HEIGHT / 2) < 0.001,
        });
        return finalFrame;
      };

      if (interaction.mode === "move") {
        setCustomExportFrame(
          applyCenterSnap({
            x: initial.x + dx,
            y: initial.y + dy,
            width: initial.width,
            height: initial.height,
          }),
        );
        return;
      }

      let next = { ...initial };
      if (interaction.mode.includes("e")) {
        next.width = initial.width + dx;
      }
      if (interaction.mode.includes("s")) {
        next.height = initial.height + dy;
      }
      if (interaction.mode.includes("w")) {
        next.x = initial.x + dx;
        next.width = initial.width - dx;
      }
      if (interaction.mode.includes("n")) {
        next.y = initial.y + dy;
        next.height = initial.height - dy;
      }

      if (next.width < MIN_CUSTOM_FRAME_SIZE) {
        if (interaction.mode.includes("w")) {
          next.x -= MIN_CUSTOM_FRAME_SIZE - next.width;
        }
        next.width = MIN_CUSTOM_FRAME_SIZE;
      }
      if (next.height < MIN_CUSTOM_FRAME_SIZE) {
        if (interaction.mode.includes("n")) {
          next.y -= MIN_CUSTOM_FRAME_SIZE - next.height;
        }
        next.height = MIN_CUSTOM_FRAME_SIZE;
      }

      setCustomExportFrame(applyCenterSnap(next));
    },
    [getStagePointFromClient],
  );

  useEffect(() => {
    if (!isLocationPicking) {
      setLocationPickHover(null);
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setLocationPickHover(null);
      onCancelLocationPick();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLocationPicking, onCancelLocationPick]);

  useEffect(() => {
    if (isLocationPicking) {
      setActiveToolTab("location");
    }
  }, [isLocationPicking]);

  const handleLocationPickerMouseMove = useCallback(
    (event) => {
      if (!isLocationPicking) return;
      const point = getWorldPointFromClient(event.clientX, event.clientY);
      setLocationPickHover(point);
    },
    [getWorldPointFromClient, isLocationPicking],
  );

  const handleLocationPickerMouseLeave = useCallback(() => {
    setLocationPickHover(null);
  }, []);

  const handleLocationPickerMouseDown = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      const point = getWorldPointFromClient(event.clientX, event.clientY);
      if (!point) return;

      setLocationPickHover(point);
      onLocationPick(point);
    },
    [getWorldPointFromClient, onLocationPick],
  );

  const startCustomFrameInteraction = useCallback(
    (event, mode) => {
      event.preventDefault();
      event.stopPropagation();
      const point = getStagePointFromClient(event.clientX, event.clientY);
      if (!point) return;
      customFrameInteractionRef.current = {
        mode,
        startPoint: point,
        initialFrame: normalizedCustomExportFrame,
      };
    },
    [getStagePointFromClient, normalizedCustomExportFrame],
  );

  const jumpToChapter = useCallback(
    (chapter) => {
      seek(chapter.time);
      audioEngine.seekAudio(chapter.time);
      showChapterToast(chapter.title);
    },
    [audioEngine, seek, showChapterToast],
  );

  const jumpPrevChapter = useCallback(() => {
    if (!chapters.length) return;
    const prev = [...chapters]
      .reverse()
      .find((c) => c.time < currentTime - 0.5);
    if (prev) jumpToChapter(prev);
    else jumpToChapter(chapters[0]);
  }, [chapters, currentTime, jumpToChapter]);

  const jumpNextChapter = useCallback(() => {
    if (!chapters.length) return;
    const next = chapters.find((c) => c.time > currentTime + 0.1);
    if (next) jumpToChapter(next);
  }, [chapters, currentTime, jumpToChapter]);

  const handleScrubberMouseMove = useCallback(
    (e) => {
      if (!duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width),
      );
      const hoverTime = ratio * duration;
      const chapter = getCurrentChapter(hoverTime);
      setScrubberHover({
        xPct: ratio * 100,
        label: chapter ? chapter.title : getDisplayTime(hoverTime),
      });
    },
    [duration, getCurrentChapter, getDisplayTime],
  );

  const handleScrubberMouseLeave = useCallback(() => {
    setScrubberHover(null);
  }, []);

  const toggleFullscreen = () => {
    const el = stageWrapperRef.current;
    if (!el) return;

    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const revealControls = () => {
    if (document.fullscreenElement) return;
    setShowControls(true);
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      if (document.fullscreenElement) {
        setShowControls(false);
      } else {
        setShowControls(true);
      }
      setLayoutTick((n) => n + 1);
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    setExportStartTime("0");
    setExportEndTime(duration > 0 ? duration.toFixed(2) : "0");
    setExportImageTime("0");
    setCustomExportFrame(createDefaultCustomFrame());
  }, [animation?.metadata?.animationId, duration]);

  useEffect(() => {
    const validLayouts = exportLayoutOptions.map((option) => option.value);
    if (!validLayouts.includes(exportLayout)) {
      setExportLayout("landscape");
    }
  }, [exportLayout, exportLayoutOptions]);

  useEffect(() => {
    const updateStageHeight = () => {
      setLayoutTick((n) => n + 1);
    };

    updateStageHeight();
    window.addEventListener("resize", updateStageHeight);
    return () => window.removeEventListener("resize", updateStageHeight);
  }, []);

  useEffect(() => {
    if (!showSettings) return;
    const handleOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showSettings]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable
      ) {
        return;
      }
      // ✅ Also check if focus is anywhere inside the script editor section
      const scriptEditorEl = document.querySelector(
        ".animation-workbench-script",
      );
      if (scriptEditorEl?.contains(e.target)) {
        return;
      }
      const isInsideDisplay =
        stageWrapperRef.current?.contains(document.activeElement) ||
        lastInteractionInsideRef.current;
      const sortedMarkers = [...markers].sort((a, b) => a.time - b.time);
      const selectedIndex = sortedMarkers.findIndex(
        (marker) => marker.markerId === selectedMarkerId,
      );

      if (e.code === "KeyF") {
        toggleFullscreen();
      }
      if (e.code === "Space" && (document.fullscreenElement || isInsideDisplay)) {
        e.preventDefault();
        if (isPlaying) pause();
        else play();
        return;
      }
      if (e.code === "KeyR") {
        const sorted = [...markers].sort((a, b) => a.time - b.time);
        const prev = sorted
          .filter((m) => m.time < currentTime - 0.0005)
          .pop();
        const targetTime = prev ? prev.time : 0;
        reset();
        audioEngine.stopAll();
        seek(targetTime);
        audioEngine.seekAudio(targetTime);
        setLayoutTick((n) => n + 1);
        return;
      }

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.code === "KeyZ" && isInsideDisplay) {
        e.preventDefault();
        undoMarkerChange();
        return;
      }
      if (
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === "KeyZ") ||
        ((e.ctrlKey || e.metaKey) && e.code === "KeyY")
      ) {
        if (isInsideDisplay) {
          e.preventDefault();
          redoMarkerChange();
          return;
        }
      }
      if (!isInsideDisplay) return;
      if (activeToolTab === "shapes") {
        if ((e.ctrlKey || e.metaKey) && e.code === "KeyA") {
          e.preventDefault();
          handleSelectAllShapes();
          return;
        }
        if ((e.ctrlKey || e.metaKey) && e.code === "KeyC" && selectedShapeIds.length) {
          e.preventDefault();
          handleCopySelectedShapes();
          return;
        }
        if ((e.ctrlKey || e.metaKey) && e.code === "KeyV") {
          e.preventDefault();
          handlePasteShapes();
          return;
        }
        if ((e.ctrlKey || e.metaKey) && e.code === "KeyD" && selectedShapeIds.length) {
          e.preventDefault();
          handleCloneShape(selectedShapeIds);
          return;
        }
        if (e.code === "Escape" && selectedShapeIds.length) {
          e.preventDefault();
          handleClearSelectedShape();
          return;
        }
        if ((e.code === "Delete" || e.code === "Backspace") && selectedShapeIds.length) {
          e.preventDefault();
          handleDeleteShape(selectedShapeIds);
          return;
        }
        if (e.code === "KeyH" && selectedShapeIds.length) {
          e.preventDefault();
          handleToggleSelectedShapeVisibility();
          return;
        }
        if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
          if (e.code === "Digit1") {
            e.preventDefault();
            handleAddShape("rect");
            return;
          }
          if (e.code === "Digit2") {
            e.preventDefault();
            handleAddShape("circle");
            return;
          }
          if (e.code === "Digit3") {
            e.preventDefault();
            handleAddShape("diamond");
            return;
          }
          if (e.code === "Digit4") {
            e.preventDefault();
            handleAddShape("triangle");
            return;
          }
        }
      }
      if (e.code === "KeyM") {
        e.preventDefault();
        addMarker(currentTime);
        return;
      }
      if (e.code === "BracketLeft" && sortedMarkers.length) {
        e.preventDefault();
        const nextMarker =
          selectedIndex > 0
            ? sortedMarkers[selectedIndex - 1]
            : sortedMarkers.find((marker) => marker.time < currentTime - 0.0005) ||
              sortedMarkers[0];
        if (nextMarker) {
          selectMarker(nextMarker.markerId);
        }
        return;
      }
      if (e.code === "BracketRight" && sortedMarkers.length) {
        e.preventDefault();
        const nextMarker =
          selectedIndex >= 0 && selectedIndex < sortedMarkers.length - 1
            ? sortedMarkers[selectedIndex + 1]
            : sortedMarkers.find((marker) => marker.time > currentTime + 0.0005) ||
              sortedMarkers[sortedMarkers.length - 1];
        if (nextMarker) {
          selectMarker(nextMarker.markerId);
        }
        return;
      }
      if ((e.code === "Delete" || e.code === "Backspace") && selectedMarkerId) {
        e.preventDefault();
        deleteMarker(selectedMarkerId);
        return;
      }
      if (e.code === "KeyL" && selectedMarkerId) {
        e.preventDefault();
        toggleMarkerLock(selectedMarkerId);
        return;
      }
      if (e.code === "KeyI" && selectedMarkerId) {
        e.preventDefault();
        insertMarkerTime(selectedMarkerId);
        return;
      }

      revealControls();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    addMarker,
    activeToolTab,
    audioEngine,
    deleteMarker,
    handleAddShape,
    handleClearSelectedShape,
    handleCloneShape,
    handleCopySelectedShapes,
    handleDeleteShape,
    handlePasteShapes,
    handleSelectAllShapes,
    handleToggleSelectedShapeVisibility,
    insertMarkerTime,
    isPlaying,
    markers,
    pause,
    play,
    redoMarkerChange,
    reset,
    seek,
    selectMarker,
    selectedShapeIds,
    selectedMarkerId,
    toggleMarkerLock,
    undoMarkerChange,
    currentTime,
  ]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const inside = stageWrapperRef.current?.contains(event.target);
      lastInteractionInsideRef.current = Boolean(inside);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    const handleMove = (event) => {
      if (!customFrameInteractionRef.current) return;
      updateCustomFrameFromPointer(event.clientX, event.clientY);
    };
    const handleUp = () => {
      customFrameInteractionRef.current = null;
      setCustomFrameSnap({ x: false, y: false });
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [updateCustomFrameFromPointer]);

  const visibleConnectors = useMemo(() => {
    return connectors
      .filter((c) => currentTime >= c.startTime && currentTime <= c.endTime)
      .sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  }, [connectors, currentTime]);

  const currentChapter = getCurrentChapter(currentTime);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = stageInnerRef.current;
    if (!canvas || !host) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = host.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);

    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.translate(previewPan.x, previewPan.y);
    ctx.scale(previewZoom, previewZoom);

    const scale = Math.min(width / WORLD_WIDTH, height / WORLD_HEIGHT);
    const offsetX = (width - WORLD_WIDTH * scale) / 2;
    const offsetY = (height - WORLD_HEIGHT * scale) / 2;

    const stageCenterX = WORLD_CENTER_X;
    const stageCenterY = WORLD_CENTER_Y;

    const cameraZoom = resolvedCameraZoom;
    const cameraRotation = resolvedCameraRotation;
    const cameraWorldX = resolvedCameraX;
    const cameraWorldY = resolvedCameraY;

    const projectComponent = (c) => {
      const componentWorldBottomLeftX = Number(c.x || 0);
      const componentWorldBottomLeftY = Number(c.y || 0);
      const componentHeight = Number(c.height || 0);
      const componentWidth = Number(c.width || 0);
      const componentOriginX = Number(c.originX || 0);
      const componentOriginY = Number(c.originY || 0);

      const componentWorldOriginX =
        componentWorldBottomLeftX + componentOriginX;
      const componentWorldOriginY =
        componentWorldBottomLeftY + componentOriginY;
      const dx = componentWorldOriginX - cameraWorldX;
      const dy = componentWorldOriginY - cameraWorldY;

      const screenOriginX = stageCenterX + dx * cameraZoom;
      const screenOriginY = WORLD_HEIGHT - (stageCenterY + dy * cameraZoom);

      const worldX = screenOriginX - componentOriginX * cameraZoom;
      const worldY =
        screenOriginY - (componentHeight - componentOriginY) * cameraZoom;
      const worldW = componentWidth * cameraZoom;
      const worldH = componentHeight * cameraZoom;

      return {
        x: offsetX + worldX * scale,
        y: offsetY + worldY * scale,
        w: worldW * scale,
        h: worldH * scale,
        ox: componentOriginX * cameraZoom * scale,
        oy: (componentHeight - componentOriginY) * cameraZoom * scale,
      };
    };

      const projectedById = new Map();
      previewComponents.forEach((c) => {
        projectedById.set(c.animationComponentId, projectComponent(c));
      });

    ctx.save();
    ctx.translate(
      offsetX + stageCenterX * scale,
      offsetY + stageCenterY * scale,
    );
    ctx.rotate((-cameraRotation * Math.PI) / 180);
    ctx.translate(
      -(offsetX + stageCenterX * scale),
      -(offsetY + stageCenterY * scale),
    );

    visibleConnectors.forEach((conn) => {
      const from = projectedById.get(conn.from.animationComponentId);
      const to = projectedById.get(conn.to.animationComponentId);
      if (!from || !to) return;

      const fromRect = {
        cx: from.x + from.w / 2,
        cy: from.y + from.h / 2,
        width: from.w,
        height: from.h,
      };
      const toRect = {
        cx: to.x + to.w / 2,
        cy: to.y + to.h / 2,
        width: to.w,
        height: to.h,
      };

      const start = getBorderPoint(fromRect, toRect);
      const end = getBorderPoint(toRect, fromRect);

      const thickness = Number(conn.thickness || 2) * scale;
      const arrow = thickness * 2.8;
      const headBaseOffset = arrow * Math.cos(Math.PI / 6);
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const len = Math.hypot(dx, dy);
      const shaftEnd =
        len > headBaseOffset
          ? {
              x: end.x - (dx / len) * headBaseOffset,
              y: end.y - (dy / len) * headBaseOffset,
            }
          : end;

      ctx.strokeStyle = conn.color || "#ffffff";
      ctx.lineWidth = thickness;
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(shaftEnd.x, shaftEnd.y);
      ctx.stroke();

      const angle = Math.atan2(dy, dx);
      ctx.fillStyle = conn.color || "#ffffff";
      ctx.beginPath();
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(
        end.x - arrow * Math.cos(angle - Math.PI / 6),
        end.y - arrow * Math.sin(angle - Math.PI / 6),
      );
      ctx.lineTo(
        end.x - arrow * Math.cos(angle + Math.PI / 6),
        end.y - arrow * Math.sin(angle + Math.PI / 6),
      );
      ctx.closePath();
      ctx.fill();
    });

      const sorted = [...previewComponents].sort((a, b) => {
        if ((a.z || 0) !== (b.z || 0)) return (a.z || 0) - (b.z || 0);
        return 0;
      });
      sorted.forEach((c) => {
        if (!c.visibility) return;
        const p = projectedById.get(c.animationComponentId);
        if (!p) return;
        const isAxisAligned =
          Math.abs(Number(c.rotate || 0)) < 0.0001 &&
          Math.abs(cameraRotation) < 0.0001 &&
          (c.shape || "rect") === "rect";
        const baseRect = p;

        ctx.save();
        const pivotX = baseRect.x + p.ox;
        const pivotY = baseRect.y + p.oy;
        ctx.translate(pivotX, pivotY);
        ctx.rotate((Number(c.rotate || 0) * Math.PI) / 180);
        ctx.translate(-pivotX, -pivotY);
        ctx.globalAlpha = Math.max(0, Math.min(1, Number(c.opacity ?? 1)));

        buildShapePath(ctx, baseRect, c, cameraZoom, scale);
        applyShadowStyle(ctx, c, cameraZoom, scale);
        ctx.fillStyle = c.backgroundColor || "#000000";
        ctx.fill();

      const topW = Number(c.borderTopWidth || 0) * cameraZoom * scale;
      const rightW = Number(c.borderRightWidth || 0) * cameraZoom * scale;
      const bottomW = Number(c.borderBottomWidth || 0) * cameraZoom * scale;
      const leftW = Number(c.borderLeftWidth || 0) * cameraZoom * scale;
      const hasBorder = topW > 0 || rightW > 0 || bottomW > 0 || leftW > 0;
      const topType = c.borderTopType || "solid";
      const rightType = c.borderRightType || "solid";
      const bottomType = c.borderBottomType || "solid";
      const leftType = c.borderLeftType || "solid";

      const uniformSolidBorder =
          hasBorder &&
          (c.shape || "rect") === "rect" &&
          Math.abs(topW - rightW) < 0.0001 &&
        Math.abs(topW - bottomW) < 0.0001 &&
        Math.abs(topW - leftW) < 0.0001 &&
        (c.borderTopColor || "#000000") === (c.borderRightColor || "#000000") &&
        (c.borderTopColor || "#000000") ===
          (c.borderBottomColor || "#000000") &&
        (c.borderTopColor || "#000000") === (c.borderLeftColor || "#000000") &&
        topType === "solid" &&
        rightType === "solid" &&
        bottomType === "solid" &&
        leftType === "solid";

        if (uniformSolidBorder) {
          const borderColor = c.borderTopColor || "#000000";
          const bw = topW;
          ctx.save();
          drawRoundedRect(
            ctx,
            baseRect.x - bw / 2,
            baseRect.y - bw / 2,
            baseRect.w + bw,
            baseRect.h + bw,
            {
              tl: Number(c.radiusTopLeft || 0) * cameraZoom * scale + bw / 2,
              tr: Number(c.radiusTopRight || 0) * cameraZoom * scale + bw / 2,
              br: Number(c.radiusBottomRight || 0) * cameraZoom * scale + bw / 2,
              bl: Number(c.radiusBottomLeft || 0) * cameraZoom * scale + bw / 2,
            },
          );
          ctx.lineWidth = bw;
          ctx.strokeStyle = borderColor;
          ctx.setLineDash([]);
          ctx.stroke();
          ctx.restore();
        } else if ((c.shape || "rect") === "rect") {
          if (topW > 0) {
            if (isAxisAligned && topType === "solid") {
              ctx.fillStyle = c.borderTopColor || "#000000";
              ctx.fillRect(
                baseRect.x - leftW,
                baseRect.y - topW,
                baseRect.w + leftW + rightW,
                topW,
              );
            } else {
              ctx.save();
              ctx.lineWidth = topW;
              ctx.strokeStyle = c.borderTopColor || "#000000";
              ctx.lineCap = "butt";
              applyLineStyle(ctx, topType);
              const y = baseRect.y + topW / 2;
              ctx.beginPath();
              ctx.moveTo(baseRect.x, y);
              ctx.lineTo(baseRect.x + baseRect.w, y);
              ctx.stroke();
              ctx.restore();
            }
          }

          if (rightW > 0) {
            if (isAxisAligned && rightType === "solid") {
              ctx.fillStyle = c.borderRightColor || "#000000";
              ctx.fillRect(
                baseRect.x + baseRect.w,
                baseRect.y - topW,
                rightW,
                baseRect.h + topW + bottomW,
              );
            } else {
              ctx.save();
              ctx.lineWidth = rightW;
              ctx.strokeStyle = c.borderRightColor || "#000000";
              ctx.lineCap = "butt";
              applyLineStyle(ctx, rightType);
              const x = baseRect.x + baseRect.w - rightW / 2;
              ctx.beginPath();
              ctx.moveTo(x, baseRect.y);
              ctx.lineTo(x, baseRect.y + baseRect.h);
              ctx.stroke();
              ctx.restore();
            }
          }

          if (bottomW > 0) {
            if (isAxisAligned && bottomType === "solid") {
              ctx.fillStyle = c.borderBottomColor || "#000000";
              ctx.fillRect(
                baseRect.x - leftW,
                baseRect.y + baseRect.h,
                baseRect.w + leftW + rightW,
                bottomW,
              );
            } else {
              ctx.save();
              ctx.lineWidth = bottomW;
              ctx.strokeStyle = c.borderBottomColor || "#000000";
              ctx.lineCap = "butt";
              applyLineStyle(ctx, bottomType);
              const y = baseRect.y + baseRect.h - bottomW / 2;
              ctx.beginPath();
              ctx.moveTo(baseRect.x, y);
              ctx.lineTo(baseRect.x + baseRect.w, y);
              ctx.stroke();
              ctx.restore();
            }
          }

          if (leftW > 0) {
            if (isAxisAligned && leftType === "solid") {
              ctx.fillStyle = c.borderLeftColor || "#000000";
              ctx.fillRect(
                baseRect.x - leftW,
                baseRect.y - topW,
                leftW,
                baseRect.h + topW + bottomW,
              );
            } else {
              ctx.save();
              ctx.lineWidth = leftW;
              ctx.strokeStyle = c.borderLeftColor || "#000000";
              ctx.lineCap = "butt";
              applyLineStyle(ctx, leftType);
              const x = baseRect.x + leftW / 2;
              ctx.beginPath();
              ctx.moveTo(x, baseRect.y);
              ctx.lineTo(x, baseRect.y + baseRect.h);
              ctx.stroke();
              ctx.restore();
            }
          }
        } else if (hasBorder) {
          ctx.save();
          buildShapePath(ctx, baseRect, c, cameraZoom, scale);
          ctx.lineWidth = Math.max(topW, rightW, bottomW, leftW);
          ctx.strokeStyle = c.borderTopColor || "#000000";
          ctx.setLineDash([]);
          ctx.stroke();
          ctx.restore();
        }

        clearShadowStyle(ctx);

        if (c.text) {
          if (c.overflow === "hidden") {
            buildShapePath(ctx, baseRect, c, cameraZoom, scale);
            ctx.clip();
          }
          ctx.fillStyle = c.color || "#ffffff";
        const fontSize = Number(c.fontSize || 16) * cameraZoom * scale;
        const fontWeight = Number(c.fontWeight || 400);
        const fontFamily = c.fontFamily || "JetBrains Mono, Consolas, Monaco, monospace";
        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        const textPad = 8 * scale;
        const textMaxWidth = Math.max(1, baseRect.w - textPad * 2);
        const lineHeight = fontSize * Math.max(Number(c.lineHeight || 1.2), 0.1);
        const letterSpacing = Number(c.letterSpacing || 0) * cameraZoom * scale;
        const lines = wrapTextLines(ctx, c.text, textMaxWidth);
        const sampleMetrics = ctx.measureText("Mg");
        const ascent = sampleMetrics.actualBoundingBoxAscent || fontSize * 0.8;
        const descent =
          sampleMetrics.actualBoundingBoxDescent || fontSize * 0.2;
        const glyphHeight = ascent + descent;
        const baselineOffset = (lineHeight - glyphHeight) / 2 + ascent;
        const totalHeight = lines.length * lineHeight;
        const firstBaselineY =
          c.verticalAlign === "top"
            ? baseRect.y + textPad + baselineOffset
            : c.verticalAlign === "bottom"
              ? baseRect.y + baseRect.h - textPad - totalHeight + baselineOffset
              : baseRect.y + (baseRect.h - totalHeight) / 2 + baselineOffset;

        ctx.textBaseline = "alphabetic";
        if (c.textAlign === "left") {
          ctx.textAlign = "left";
          lines.forEach((line, idx) => {
            drawTextLine(
              ctx,
              line,
              baseRect.x + textPad,
              firstBaselineY + idx * lineHeight,
              letterSpacing,
            );
          });
        } else if (c.textAlign === "right") {
          ctx.textAlign = "right";
          lines.forEach((line, idx) => {
            drawTextLine(
              ctx,
              line,
              baseRect.x + baseRect.w - textPad,
              firstBaselineY + idx * lineHeight,
              letterSpacing,
            );
          });
        } else {
          ctx.textAlign = "center";
          lines.forEach((line, idx) => {
            drawTextLine(
              ctx,
              line,
              baseRect.x + baseRect.w / 2,
              firstBaselineY + idx * lineHeight,
              letterSpacing,
            );
          });
        }
      }

      ctx.restore();
    });

    ctx.restore();
  }, [
    previewComponents,
    resolvedCameraRotation,
    resolvedCameraX,
    resolvedCameraY,
    resolvedCameraZoom,
    visibleConnectors,
    currentTime,
    layoutTick,
    previewPan.x,
    previewPan.y,
    previewZoom,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(toastTimerRef.current);
    };
  }, []);

  if (!hasAnimationPayload) {
    return (
      <section
        className={`editor-display ${
          showComponentBar ? "minimize-width-display" : "full-width-display"
        } ${showTimelines ? "minimize-height-display" : "full-height-display"}`}
      />
    );
  }

  return (
    <section
      ref={stageWrapperRef}
      tabIndex={0}
      onMouseDown={() => stageWrapperRef.current?.focus()}
      className={`editor-display ${
        showComponentBar ? "minimize-width-display" : "full-width-display"
      } ${showTimelines ? "minimize-height-display" : "full-height-display"}`}
    >
      {showControls && (
        <div className="editor-display-controls">
          {!isPlaying ? (
            <button
              onClick={play}
              title="Play"
              aria-label="Play"
              className="editor-icon-btn"
            >
              <FiPlay size={14} />
            </button>
          ) : (
            <button
              onClick={pause}
              title="Pause"
              aria-label="Pause"
              className="editor-icon-btn"
            >
              <FiPause size={14} />
            </button>
          )}
          <button
            onClick={() => {
              reset();
              audioEngine.stopAll();
              if (activeTimeLock) {
                seek(effectiveLockedTimeRef.current || 0);
                audioEngine.seekAudio(effectiveLockedTimeRef.current || 0);
              }
            }}
            title="Reset"
            aria-label="Reset"
            className="editor-icon-btn"
          >
            <FiRotateCcw size={14} />
          </button>

          {duration > 0 && (
            <div
              className="editor-scrubber-wrapper"
              onMouseMove={handleScrubberMouseMove}
              onMouseLeave={handleScrubberMouseLeave}
            >
              <input
                type="range"
                min={0}
                max={duration}
                step={0.01}
                value={currentTime}
                onChange={(e) => {
                  const t = Number(e.target.value);
                  seek(t);
                  audioEngine.seekAudio(t);
                }}
                disabled={isPlaying}
              />
              {chapters.map((c, i) => (
                <div
                  key={i}
                  className="editor-chapter-dot"
                  style={{ left: `${(c.time / duration) * 100}%` }}
                  title={c.title}
                />
              ))}
              {scrubberHover && (
                <div
                  className="editor-scrubber-tooltip"
                  style={{ left: `${scrubberHover.xPct}%` }}
                >
                  {scrubberHover.label}
                </div>
              )}
              {chapterToast && (
                <div className="editor-chapter-toast" key={chapterToast}>
                  {chapterToast}
                </div>
              )}
            </div>
          )}

          <span className="editor-playback-time">
            {currentChapter ? (
              <>
                <span
                  className="editor-current-chapter-inline"
                  title={currentChapter.title}
                >
                  {currentChapter.title}
                </span>
                <span>&middot;</span>
              </>
            ) : null}
            <span>{getDisplayTime(currentTime)}</span>
            <span>/</span>
            <span>{getDisplayTime(duration)}</span>
          </span>

          <div className="editor-settings-wrapper" ref={settingsRef}>
            {(isExporting || exportError) && (
              <div className="editor-export-toolbar-status" aria-live="polite">
                {isExporting ? (
                  <>
                    <button
                      className="editor-export-toolbar-pill"
                      style={{ "--export-progress": `${exportProgress}%` }}
                      onClick={() => setShowSettings(true)}
                      title="Open export details"
                    >
                      <span className="editor-export-toolbar-text">
                        {exportToolbarLabel}
                      </span>
                    </button>
                    <span className="editor-export-toolbar-phase">
                      {exportToolbarPhase || "processing"}
                    </span>
                    <button
                      className="editor-settings-chip cancel-chip editor-icon-btn"
                      onClick={cancelExport}
                      title="Cancel export"
                    >
                      <FiX size={13} />
                    </button>
                  </>
                ) : (
                  <span className="editor-export-toolbar-error">
                    {exportError}
                  </span>
                )}
              </div>
            )}
            <button
              className={`editor-settings-btn editor-icon-btn ${showSettings ? "active" : ""}`}
              onClick={() => setShowSettings((v) => !v)}
              title="Settings"
            >
              <FiSettings size={14} />
            </button>

            {showSettings && (
              <div className="editor-settings-panel">
                <div className="editor-settings-label">Speed</div>
                <div className="editor-settings-row">
                  {[0.25, 0.5, 1, 2, 4].map((s) => (
                    <button
                      key={s}
                      className={`editor-settings-chip ${speed === s ? "active" : ""}`}
                      onClick={() => handleSetSpeed(s)}
                    >
                      {s}x
                    </button>
                  ))}
                </div>

                {chapters.length > 0 && (
                  <>
                    <div className="editor-settings-label editor-settings-label-spaced">
                      Chapters
                    </div>
                    <div className="editor-settings-row">
                      <button
                        className="editor-settings-chip editor-chip-with-icon"
                        onClick={() => {
                          jumpPrevChapter();
                          setShowSettings(false);
                        }}
                      >
                        <FiSkipBack size={13} /> Prev
                      </button>
                      <button
                        className="editor-settings-chip editor-chip-with-icon"
                        onClick={() => {
                          jumpNextChapter();
                          setShowSettings(false);
                        }}
                      >
                        Next <FiSkipForward size={13} />
                      </button>
                    </div>
                    <div className="editor-settings-chapter-list">
                      {chapters.map((c, i) => (
                        <button
                          key={i}
                          className={`editor-settings-chapter-btn ${
                            currentChapter?.title === c.title ? "active" : ""
                          }`}
                          onClick={() => {
                            jumpToChapter(c);
                            setShowSettings(false);
                          }}
                        >
                          <span className="editor-chapter-time">
                            {getDisplayTime(c.time)}
                          </span>
                          <span className="editor-chapter-title">
                            {c.title}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="editor-settings-label editor-settings-label-spaced">
                  Export
                </div>
                <div className="editor-settings-row">
                  <select
                    className="editor-settings-select"
                    value={exportType}
                    onChange={(e) => setExportType(e.target.value)}
                    disabled={isExporting}
                  >
                    {EXPORT_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="editor-settings-select"
                    value={exportQuality}
                    onChange={(e) => setExportQuality(e.target.value)}
                    disabled={
                      isExporting ||
                      (exportType === "image" && exportLayout === "custom_size")
                    }
                  >
                    <option value="1080p">1080p</option>
                    <option value="2K">2K</option>
                    <option value="4K">4K</option>
                  </select>
                  <select
                    className="editor-settings-select"
                    value={exportLayout}
                    onChange={(e) => setExportLayout(e.target.value)}
                    disabled={isExporting}
                  >
                    {exportLayoutOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {exportType === "image" ? (
                  <div className="editor-settings-range-grid">
                    <label className="editor-settings-field">
                      <span className="editor-settings-field-label">Time</span>
                      <input
                        className="editor-settings-input"
                        type="number"
                        min="0"
                        max={duration || undefined}
                        step="0.1"
                        value={exportImageTime}
                        onChange={(e) => setExportImageTime(e.target.value)}
                        disabled={isExporting}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="editor-settings-range-grid">
                    <label className="editor-settings-field">
                      <span className="editor-settings-field-label">Start</span>
                      <input
                        className="editor-settings-input"
                        type="number"
                        min="0"
                        max={duration || undefined}
                        step="0.1"
                        value={exportStartTime}
                        onChange={(e) => setExportStartTime(e.target.value)}
                        disabled={isExporting}
                      />
                    </label>
                    <label className="editor-settings-field">
                      <span className="editor-settings-field-label">End</span>
                      <input
                        className="editor-settings-input"
                        type="number"
                        min="0"
                        max={duration || undefined}
                        step="0.1"
                        value={exportEndTime}
                        onChange={(e) => setExportEndTime(e.target.value)}
                        disabled={isExporting}
                      />
                    </label>
                  </div>
                )}
                <div className="editor-settings-row">
                  {exportType === "image" ? (
                    <button
                      className="editor-settings-chip"
                      onClick={resetExportImageTime}
                      disabled={isExporting}
                    >
                      Time = playhead
                    </button>
                  ) : (
                    <>
                      <button
                        className="editor-settings-chip"
                        onClick={() => applyCurrentTimeToExportBound("start")}
                        disabled={isExporting}
                      >
                        Start = playhead
                      </button>
                      <button
                        className="editor-settings-chip"
                        onClick={() => applyCurrentTimeToExportBound("end")}
                        disabled={isExporting}
                      >
                        End = playhead
                      </button>
                      <button
                        className="editor-settings-chip"
                        onClick={resetExportRange}
                        disabled={isExporting}
                      >
                        Full range
                      </button>
                    </>
                  )}
                  {requiresCustomFrame && (
                    <button
                      className="editor-settings-chip"
                      onClick={resetCustomExportFrame}
                      disabled={isExporting}
                    >
                      Reset frame
                    </button>
                  )}
                </div>
                <div
                  className={`editor-settings-help ${exportRangeError || exportFrameError ? "error" : ""}`}
                >
                  {exportRangeError ||
                    exportFrameError ||
                    (requiresCustomFrame
                      ? exportType === "image" && exportLayout === "custom_size"
                        ? "Drag the selection box on the preview. The exported image will match the selected area exactly with no padding or crop."
                        : "Drag the crop box on the preview. The selected area will be preserved fully and padded to fit portrait."
                      : exportType === "image"
                        ? `Image time: ${(parsedExportImageTime ?? 0).toFixed(2)}s`
                        : `Clip duration: ${Math.max(
                            0,
                            (parsedExportEndTime ?? 0) - (parsedExportStartTime ?? 0),
                          ).toFixed(2)}s`)}
                </div>
                <div className="editor-settings-row">
                  <button
                    className={`editor-settings-chip export-chip ${isExporting ? "exporting" : ""}`}
                    style={{ "--export-progress": `${exportProgress}%` }}
                    onClick={exportMedia}
                    disabled={isExporting || !canExport}
                  >
                    {isExporting ? (
                      `${exportProgress}% · ${formatEta(exportEtaSeconds)}`
                    ) : (
                      <span className="editor-chip-with-icon">
                        <FiDownload size={13} /> {exportType === "image" ? "Export image" : "Export video"}
                      </span>
                    )}
                  </button>
                  {isExporting && (
                    <span className="editor-export-phase">
                      {exportPhase || "processing"}
                    </span>
                  )}
                  {!isExporting && exportError && (
                    <span className="editor-export-error">{exportError}</span>
                  )}
                  {isExporting && (
                    <button
                      className="editor-settings-chip cancel-chip editor-icon-btn"
                      onClick={cancelExport}
                    >
                      <FiX size={13} />
                    </button>
                  )}
                </div>

                <div className="editor-settings-label editor-settings-label-spaced">
                  View
                </div>
                <div className="editor-settings-row">
                  <button
                    className="editor-settings-chip editor-chip-with-icon"
                    onClick={() => {
                      toggleFullscreen();
                      setShowSettings(false);
                    }}
                  >
                    <FiMaximize size={13} /> Fullscreen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div
        ref={stageInnerRef}
        className={showTimelines ? "editor-stage" : "editor-stage-full-view"}
        onWheel={handleStageWheelZoom}
      >
        <div
          className="editor-stage-world"
        >
          <canvas ref={canvasRef} className="editor-stage-canvas" />
          {activeToolTab === "shapes" &&
            !isLocationPicking &&
            !requiresCustomFrame && (
              <div
                className="editor-shape-overlay"
                onMouseDown={handleShapeOverlayBackgroundMouseDown}
              >
                <div
                  className="editor-shape-overlay-scene"
                  style={{ transform: `rotate(${-resolvedCameraRotation}deg)` }}
                >
                  {shapeOverlayItems.map((shape) => {
                    const isSelected = selectedShapeIds.includes(shape.shapeId);
                    return (
                      <div
                        key={shape.shapeId}
                        className={`editor-shape-overlay-item${
                          isSelected ? " selected" : ""
                        }`}
                        style={{
                          left: `${shape.x}px`,
                          top: `${shape.y}px`,
                          width: `${shape.w}px`,
                          height: `${shape.h}px`,
                          transform: `rotate(${shape.rotate}deg)`,
                          borderColor: isSelected ? undefined : "transparent",
                          boxShadow: isSelected ? undefined : "none",
                        }}
                      >
                        <button
                          type="button"
                          className="editor-shape-overlay-hitbox"
                          onMouseDown={(event) =>
                            startShapeInteraction(event, shape.shapeId, "move")
                          }
                          onClick={(event) =>
                            handleSelectShape(shape.shapeId, {
                              toggle: Boolean(event.shiftKey),
                            })
                          }
                          aria-label={`Edit ${shape.name}`}
                        />
                        {isSelected &&
                          ["nw", "ne", "se", "sw"].map((handle) => (
                            <button
                              key={handle}
                              type="button"
                              className={`editor-shape-overlay-handle ${handle}`}
                              onMouseDown={(event) =>
                                startShapeInteraction(event, shape.shapeId, handle)
                              }
                              aria-label={`Resize ${shape.name} from ${handle}`}
                            >
                              <span className={`editor-shape-overlay-corner ${handle}`} />
                            </button>
                          ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          {isLocationPicking && (
            <button
              type="button"
              className="editor-location-picker-overlay"
              onMouseDown={handleLocationPickerMouseDown}
              onMouseMove={handleLocationPickerMouseMove}
              onMouseLeave={handleLocationPickerMouseLeave}
              aria-label="Pick a world location from the preview"
            >
              <span className="editor-location-picker-pill">
                {locationPickHover
                  ? `X ${formatWorldCoordinate(locationPickHover.x)} | Y ${formatWorldCoordinate(locationPickHover.y)}`
                  : "Click preview to pick location"}
              </span>
            </button>
          )}
        </div>
        {previewZoom > 1.001 && (
          <button
            type="button"
            className="editor-stage-zoom-reset"
            onClick={() => {
              setPreviewZoom(1);
              setPreviewPan({ x: 0, y: 0 });
            }}
          >
            {`${Math.round(previewZoom * 100)}%`}
          </button>
        )}
        {requiresCustomFrame && (
          <div className="editor-custom-frame-overlay">
            <div
              className={`editor-custom-frame-stage-guide vertical ${customFrameSnap.x ? "active" : ""}`}
            />
            <div
              className={`editor-custom-frame-stage-guide horizontal ${customFrameSnap.y ? "active" : ""}`}
            />
            <div
              className={`editor-custom-frame ${customFrameSnap.x || customFrameSnap.y ? "snapped" : ""}`}
              style={{
                left: `${(normalizedCustomExportFrame.x / WORLD_WIDTH) * 100}%`,
                top: `${(normalizedCustomExportFrame.y / WORLD_HEIGHT) * 100}%`,
                width: `${(normalizedCustomExportFrame.width / WORLD_WIDTH) * 100}%`,
                height: `${(normalizedCustomExportFrame.height / WORLD_HEIGHT) * 100}%`,
              }}
              onMouseDown={(event) => startCustomFrameInteraction(event, "move")}
            >
              <div className="editor-custom-frame-label">
                {`${normalizedCustomExportFrame.width.toFixed(0)} x ${normalizedCustomExportFrame.height.toFixed(0)}`}
              </div>
              <div className="editor-custom-frame-center-guide vertical" />
              <div className="editor-custom-frame-center-guide horizontal" />
              {["nw", "ne", "se", "sw"].map((handle) => (
                <button
                  key={handle}
                  type="button"
                  className={`editor-custom-frame-handle ${handle}`}
                  onMouseDown={(event) =>
                    startCustomFrameInteraction(event, handle)
                  }
                  aria-label={`Resize frame ${handle}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

        <div className="editor-display-tooltray">
          <div className="editor-display-tooltabs" role="tablist" aria-label="Display tools">
              {[ 
                { key: "waveform", label: "Waveform" },
                { key: "shapes", label: "Shapes", icon: FiBox },
                { key: "color", label: "Color", icon: FiDroplet },
                { key: "location", label: "Location", icon: FiMapPin },
                { key: "camera", label: "Camera", icon: FiVideo },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeToolTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`editor-display-tooltab${isActive ? " active" : ""}`}
                  onClick={() => setActiveToolTab(tab.key)}
                >
                  {Icon ? <Icon size={12} /> : null}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
          <div className="editor-display-toolpanel">
            {activeToolTab === "waveform" ? (
              <AudioWaveform
                audioTracks={audioTracks}
                assets={assets}
                currentScriptText={currentScriptText}
                duration={duration}
                currentTime={currentTime}
                isPlaying={isPlaying}
                markers={markers}
                selectedMarkerId={selectedMarkerId}
                lockedMarkerId={lockedMarkerId}
                onAddMarker={addMarker}
                onUpdateMarker={updateMarker}
                onDeleteMarker={deleteMarker}
                onToggleMarkerLock={toggleMarkerLock}
                onReplaceMarkers={replaceMarkers}
                onInsertMarkerTime={insertMarkerTime}
                onPlayFromMarker={playFromMarker}
                onBeginMarkerDrag={beginMarkerDrag}
                onSelectMarker={selectMarker}
                onSetSegmentBoundary={setSegmentBoundary}
                onPlaySegmentPreview={playSegmentPreview}
                onClearSegmentPreview={clearSegmentPreview}
                segmentStartMarkerId={segmentStartMarkerId}
                segmentEndMarkerId={segmentEndMarkerId}
                canUndo={markerUndoStackRef.current.length > 0}
                canRedo={markerRedoStackRef.current.length > 0}
                onUndo={undoMarkerChange}
                onRedo={redoMarkerChange}
                onSeek={(time) => {
                  seek(time);
                  audioEngine.seekAudio(time);
                }}
              />
            ) : (
              <EditorToolsBar
                activeTab={activeToolTab}
                location={location}
                isLocationPicking={isLocationPicking}
                onLocationInputChange={onLocationInputChange}
                onToggleLocationPicking={onToggleLocationPicking}
                camera={toolCamera}
                isCameraOverrideActive={isCameraOverrideActive}
                onCameraInputChange={onCameraInputChange}
                onSyncCameraToLive={onSyncCameraToLive}
                onResetCameraOverride={onResetCameraOverride}
                shapes={shapeToolItems}
                selectedShapeIds={selectedShapeIds}
                selectedShapeId={primarySelectedShapeId}
                onSelectShape={handleSelectShape}
                onAddShape={handleAddShape}
                onCloneShape={handleCloneShape}
                onCopyShapes={handleCopySelectedShapes}
                onPasteShapes={handlePasteShapes}
                onDeleteShape={handleDeleteShape}
                onResetShape={handleResetShape}
                onShapeFieldChange={handleShapeFieldChange}
                buildSelectedShapeDsl={buildSelectedShapeDsl}
                buildAllShapesDsl={buildAllShapesDsl}
                onInsertScriptSnippet={onInsertScriptSnippet}
              />
            )}
          </div>
        </div>
      </section>
    );
  };

export default CanvasEditorDisplay;
